// src/components/workspace/ProposalWorkspace.tsx
// AI-First Proposal Workspace with 3-column layout:
// Left: Content navigation | Center: Proposal content | Right: Collapsible AI tools

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  X, Download, Save, Eye, Edit2, RefreshCw, FileText, Zap, Building2,
  CheckCircle, AlertTriangle, AlertCircle, Clock, Menu, ArrowLeft,
  Calendar, Sparkles, Target, Loader2, TrendingUp, Database,
  Map as MapIcon, ExternalLink, ArrowRight, Info, Package,
  MessageSquare, DollarSign, Send, ChevronLeft, ChevronRight, ChevronDown,
  PanelLeftClose, PanelLeft, PanelRightClose, PanelRight,
  Users, Shield, Briefcase, BarChart3, Layers, MoreVertical, ListChecks
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { ProposalExportService } from '../../services/ProposalExportService';
import { BeautifulDocxExporter } from '../../services/BeautifulDocxExporter';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import { PreviewComponents } from '../shared/MarkdownComponents';
import { smartNormalize, formatCoverLetter } from '../../lib/markdown-utils';
import { CitationHighlight } from '../CitationPopover';
import {
  ProposalSectionRenderer,
  AIFeedbackPanel,
  SectionFeedbackDialog,
  ProfileSourceModal,
  DeliverablesSectionContent,
  DeliverableFeedbackDialog,
  ProductFeedbackDialog,
  ExportDialog,
  BuildHandoffDialog,
} from '../proposal';
import { ProductSynthesis } from '../proposal/ProductSynthesis';
import { JobDescriptionProvider } from '../personnel/JobDescriptionContext';
import { ProposalEditProvider, setByPath } from '../proposal/ProposalEditContext';
import { normalizeCoverLetterString } from '../../lib/normalize-cover-letter';
import { GapResolutionCenter } from './GapResolutionCenter';
import { AiEditReviewDialog, type AiEditState } from './AiEditReviewDialog';
import { RequirementCoveragePanel } from './RequirementCoveragePanel';
import { PlaceholderReviewDialog } from './PlaceholderReviewDialog';
import { buildPlaceholderResolver, resolveProposalPlaceholders, sectionsWithPlaceholders, type PlaceholderScan } from '../../lib/placeholders';
import type { RequirementCoverage, RequirementCoverageItem } from '../../types/source-grounded';
import { useProposalAnalysis } from '../../hooks/useProposalAnalysis';
import { getWorkspaceAIService, WorkspaceAIService, ChatMessage } from '../../services/workspace-ai-service';
import type { SectionFeedback } from '../proposal/SectionFeedbackDialog';
import type { DeliverableFeedback } from '../proposal/DeliverableFeedbackDialog';
import type { ProductFeedback } from '../proposal/ProductFeedbackDialog';
import type { DeliverablesData } from '../../types/deliverables';
import type { AIFeedback, ProposalSection } from '../../services/orchestrated-proposal-service';
import { OrchestratedProposalService } from '../../services/orchestrated-proposal-service';
import { RecomputePricingReviewDialog, type RecomputeSummaryRow } from '../proposal/RecomputePricingReviewDialog';
import type {
  SourceGroundedProposalOutput,
  GapReport,
  SourceMap,
  WinAssessment,
  ImprovementRoadmap,
  CriticalGap,
  ImprovementOpportunity,
  ProposalStrength,
  SubmitabilityVerdict,
} from '../../types/source-grounded';

interface ProposalTemplate {
  id: string;
  template_name: string;
  notice_id: string;
  content_json: any;
  template_url: string;
  proposal_type: 'basic' | 'enhanced' | 'source-grounded' | 'orchestrated';
  enhanced_data?: any;
  created_at: string;
  updated_at: string;
  contract?: any;
}

// Right panel tool types
type RightPanelTool = 'chat' | 'gaps' | 'coverage' | 'analysis' | 'costing';

// Left nav section types
type LeftNavSection = 'content' | 'gaps' | 'sources' | 'evidence' | 'roadmap';

// Build a before → after summary of a pricing recompute from the old + new pricing-section
// content (each a stringified pricingResponse). Pure + defensive so a malformed payload
// degrades to a sparse summary rather than throwing.
function buildPricingRecomputeSummary(oldContent: string | undefined, newContent: string): RecomputeSummaryRow[] {
  const parse = (s: string | undefined): any => {
    if (!s) return {};
    try { return JSON.parse(s); } catch { return {}; }
  };
  const o = parse(oldContent);
  const n = parse(newContent);
  const fields: Array<{ label: string; get: (p: any) => string }> = [
    { label: 'Pricing model', get: p => p?.pricingMode?.model || (p?.pricingMode?.commercial ? 'commercial' : (p?.farTable15_2CoverPage ? 'cost-buildup' : '')) },
    { label: 'FAR part', get: p => p?.pricingMode?.farPart ? `FAR ${p.pricingMode.farPart}` : '' },
    { label: 'CLIN line items', get: p => { const c = p?.clinPriceSchedule?.clins?.length ?? p?.clinStructure?.length; return c != null ? String(c) : ''; } },
    { label: 'Proposed total', get: p => p?.farTable15_2CoverPage?.proposedCostSummary?.totalPrice || p?.priceValidation?.proposedTotal || '' },
    { label: 'Guaranteed minimum', get: p => p?.priceValidation?.guaranteedMinimum || '' },
    { label: 'Maximum value', get: p => p?.priceValidation?.maximumValue || '' },
    { label: 'Below floor?', get: p => p?.priceValidation?.belowGuaranteedMinimum == null ? '' : (p.priceValidation.belowGuaranteedMinimum ? 'Yes' : 'No') },
    { label: 'Floor gap', get: p => p?.priceValidation?.reconciliation?.gapToMinimum || '' },
    { label: 'Solicitation #', get: p => p?.farTable15_2CoverPage?.solicitationNumber || '' },
    { label: 'Offeror name', get: p => p?.farTable15_2CoverPage?.offerorInfo?.name || '' },
    { label: 'Offeror address', get: p => p?.farTable15_2CoverPage?.offerorInfo?.address || '' },
  ];
  return fields
    .map(f => { const before = f.get(o); const after = f.get(n); return { label: f.label, before, after, changed: before !== after }; })
    .filter(r => r.before || r.after); // hide fields absent in both
}

export function ProposalWorkspace() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  // Core state
  const [proposal, setProposal] = useState<ProposalTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Layout state
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [rightPanelTool, setRightPanelTool] = useState<RightPanelTool>('chat');
  const [showMobileLeftNav, setShowMobileLeftNav] = useState(false);
  const [showSectionDropdown, setShowSectionDropdown] = useState(false);

  // AI-proposed section edit awaiting user approval. The AI Tools draft a change
  // but it is NEVER written to the proposal until the user clicks Apply here.
  const [aiEdit, setAiEdit] = useState<AiEditState | null>(null);
  const [applyingAiEdit, setApplyingAiEdit] = useState(false);

  // Requirement coverage matrix (AI Tools → Coverage tab).
  const [coverage, setCoverage] = useState<RequirementCoverage | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  // Deterministic placeholder resolver (Gaps tab) — fills [COMPANY NAME]/[UEI]/etc.
  const [placeholderScan, setPlaceholderScan] = useState<PlaceholderScan | null>(null);
  const [applyingPlaceholders, setApplyingPlaceholders] = useState(false);

  // Live-demonstration link (e.g. a deployed demo built from the Build Handoff).
  // Stored top-level on content_json.demoLink; shown as a callout and carried into exports.
  const [editingDemoLink, setEditingDemoLink] = useState(false);
  const [demoLinkDraft, setDemoLinkDraft] = useState('');

  // Content navigation state
  const [activeSection, setActiveSection] = useState<LeftNavSection>('content');
  const [activeTab, setActiveTab] = useState('contractAnalysis');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showHandoffDialog, setShowHandoffDialog] = useState(false);

  // Edit state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState<any>(null);
  const [recomputingPricing, setRecomputingPricing] = useState(false);
  const [recomputeReview, setRecomputeReview] = useState<{ pricing: ProposalSection; compliance: ProposalSection; rows: RecomputeSummaryRow[] } | null>(null);
  const [runningRedTeam, setRunningRedTeam] = useState(false);
  const [savingDeliverables, setSavingDeliverables] = useState(false);
  const [applyingRecompute, setApplyingRecompute] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Section regeneration state
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showProfileSourceModal, setShowProfileSourceModal] = useState(false);
  const [regeneratingSectionType, setRegeneratingSectionType] = useState<string | null>(null);
  const [regeneratingSectionTitle, setRegeneratingSectionTitle] = useState<string>('');
  const [regenerating, setRegenerating] = useState(false);

  // Source-grounded specific state
  const [showCitations, setShowCitations] = useState(true);

  // Deliverables state
  const [enhancingDeliverable, setEnhancingDeliverable] = useState(false);
  const [enhancingDeliverableId, setEnhancingDeliverableId] = useState<string | null>(null);
  const [regeneratingDeliverables, setRegeneratingDeliverables] = useState(false);
  const [showDeliverableFeedbackDialog, setShowDeliverableFeedbackDialog] = useState(false);
  const [showProductFeedbackDialog, setShowProductFeedbackDialog] = useState(false);
  const [enhancingDeliverableSection, setEnhancingDeliverableSection] = useState(false);
  const [enhancingProductSection, setEnhancingProductSection] = useState(false);
  const [deliverableFeedbackMode, setDeliverableFeedbackMode] = useState<'section' | 'individual'>('section');
  const [selectedDeliverableForEnhancement, setSelectedDeliverableForEnhancement] = useState<{id: string; name: string} | null>(null);

  // AI Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatServiceRef = useRef<WorkspaceAIService | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Contract requirements from database
  const [contractRequirements, setContractRequirements] = useState<any[]>([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);

  // Refs
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const leftSidebarRef = useRef<HTMLDivElement>(null);
  const sectionDropdownRef = useRef<HTMLDivElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);

  // Use the proposal analysis hook
  const {
    analysis,
    loading: analysisLoading,
    refreshing: analysisRefreshing,
    refreshAnalysis,
  } = useProposalAnalysis(id);

  // Initialize AI Chat service
  useEffect(() => {
    chatServiceRef.current = getWorkspaceAIService();
  }, []);

  // Update chat context when proposal changes
  useEffect(() => {
    if (proposal && chatServiceRef.current) {
      chatServiceRef.current.setContext({
        proposalId: proposal.id,
        proposalTitle: proposal.contract?.title || proposal.template_name,
        contractAgency: proposal.contract?.agency || '',
        companyName: profile?.company_name,
      });

      // Set full proposal data for rich AI context
      const sgOutput = proposal?.content_json?.sourceGroundedOutput;
      chatServiceRef.current.setProposalData({
        contract: proposal.contract,
        contentJson: proposal.content_json,
        gapReport: sgOutput?.gapReport || proposal?.content_json?.gapReport,
        winAssessment: sgOutput?.winAssessment,
        extractedRequirements: sgOutput?.extractedRequirements || [],
      });
    }
  }, [proposal, profile]);

  // Update chat context when section changes
  useEffect(() => {
    if (chatServiceRef.current) {
      const content = getContent(activeTab);
      chatServiceRef.current.updateSection(
        activeTab,
        getCurrentSectionTitle(),
        content
      );
    }
  }, [activeTab]);

  // Scroll chat to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Fetch contract requirements from database
  useEffect(() => {
    async function fetchContractRequirements() {
      if (!proposal?.contract?.notice_id && !proposal?.contract?.id) return;

      setLoadingRequirements(true);
      try {
        const contractId = proposal.contract.notice_id || proposal.contract.id;

        // Try to fetch from contract_requirements table
        const { data: requirements, error } = await supabase
          .from('contract_requirements')
          .select('*')
          .eq('contract_id', contractId)
          .order('priority', { ascending: true });

        if (error) {
          console.log('No contract_requirements found:', error.message);
        } else if (requirements && requirements.length > 0) {
          setContractRequirements(requirements);
        }
      } catch (err) {
        console.error('Error fetching contract requirements:', err);
      } finally {
        setLoadingRequirements(false);
      }
    }

    fetchContractRequirements();
  }, [proposal?.contract?.notice_id, proposal?.contract?.id]);

  // Load proposal data
  useEffect(() => {
    if (id && profile) loadProposal();
  }, [id, profile]);

  // Sync editedContent with proposal
  useEffect(() => {
    if (proposal?.content_json) {
      setEditedContent(proposal.content_json);
      setCoverage(proposal.content_json.requirementCoverage ?? null);
    }
  }, [proposal?.content_json]);

  // Close menus on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
      if (leftSidebarRef.current && !leftSidebarRef.current.contains(event.target as Node) && showMobileLeftNav) {
        setShowMobileLeftNav(false);
      }
      if (sectionDropdownRef.current && !sectionDropdownRef.current.contains(event.target as Node)) {
        setShowSectionDropdown(false);
      }
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(false);
      }
    }

    if (showExportMenu || showMobileLeftNav || showSectionDropdown || showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu, showMobileLeftNav, showSectionDropdown, showActionsMenu]);

  const loadProposal = async () => {
    try {
      if (!profile || !id) return;

      const { data: template, error: templateError } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('id', id)
        .eq('profile_id', profile.id)
        .single();

      if (templateError) throw templateError;

      // Load contract details
      let { data: contract } = await supabase
        .from('sam_contracts')
        .select('*')
        .eq('notice_id', template.notice_id)
        .maybeSingle();

      if (!contract) {
        const result = await supabase
          .from('sam_contracts')
          .select('*')
          .eq('id', template.notice_id)
          .maybeSingle();
        contract = result.data;
      }

      // Determine proposal type
      let proposalType: 'basic' | 'enhanced' | 'source-grounded' | 'orchestrated' = 'basic';
      if (
        template.content_json?.proposalType === 'source-grounded' ||
        template.content_json?.sourceGroundedOutput ||
        template.template_name?.includes('Source-Grounded')
      ) {
        proposalType = 'source-grounded';
      } else if (
        template.content_json?.aiFeedback ||
        template.content_json?.proposalType === 'orchestrated' ||
        template.content_json?.technicalSections?.some((s: any) =>
          s.title === 'Technical Approach' || s.title === 'Management Approach'
        )
      ) {
        proposalType = 'orchestrated';
      } else if (
        template.proposal_type === 'enhanced' ||
        template.content_json?.contractAnalysis ||
        template.template_name?.includes('Enhanced')
      ) {
        proposalType = 'enhanced';
      }

      // Deterministically correct the cover-letter header (date / Contracting
      // Officer / office address / subject) from the contract record. Fixes
      // proposals generated before the recipient/date fix, with no API call.
      let contentJson = template.content_json;
      if (contentJson && contract) {
        const fixedCover = normalizeCoverLetterString(contentJson.coverLetter, contract);
        if (fixedCover !== contentJson.coverLetter) {
          contentJson = { ...contentJson, coverLetter: fixedCover };
          // Persist the correction so exports and other views get it too.
          supabase
            .from('proposal_templates')
            .update({ content_json: contentJson, updated_at: new Date().toISOString() })
            .eq('id', template.id)
            .then(undefined, (e: any) => console.error('Cover-letter normalize save failed:', e));
        }
      }

      setProposal({
        ...template,
        proposal_type: proposalType,
        contract,
        content_json: contentJson
      });
      setEditedContent(contentJson);
    } catch (error) {
      console.error('Error loading proposal:', error);
      toast.error('Failed to load proposal');
      navigate('/workspace');
    } finally {
      setLoading(false);
    }
  };

  // Detect proposal types
  const isSourceGrounded = useMemo(() => {
    return proposal?.proposal_type === 'source-grounded' ||
           proposal?.content_json?.sourceGroundedOutput ||
           proposal?.content_json?.coverLetter?.includes('[PP-');
  }, [proposal]);

  const isOrchestrated = useMemo(() => {
    return proposal?.proposal_type === 'orchestrated' ||
           !!proposal?.content_json?.aiFeedback ||
           proposal?.content_json?.technicalSections?.some((s: any) =>
             s.title === 'Technical Approach' || s.title === 'Management Approach'
           );
  }, [proposal]);

  // Extract source-grounded data
  const sourceGroundedOutput = proposal?.content_json?.sourceGroundedOutput as SourceGroundedProposalOutput | undefined;
  const sourceMap = sourceGroundedOutput?.sourceMap;
  // Red Team output is persisted at top-level content_json (orchestrated path), so
  // fall back to it for gapReport/winAssessment/improvementRoadmap as well.
  const gapReport = sourceGroundedOutput?.gapReport || proposal?.content_json?.gapReport;
  const winAssessment = sourceGroundedOutput?.winAssessment || proposal?.content_json?.winAssessment;
  const improvementRoadmap = sourceGroundedOutput?.improvementRoadmap || proposal?.content_json?.improvementRoadmap;
  // Adversarial Red Team verdict (latest + prior), when one has been run.
  const redTeamAssessment = sourceGroundedOutput?.redTeamAssessment || proposal?.content_json?.redTeamAssessment;
  const redTeamVerdict = redTeamAssessment?.current;
  // Snapshot of the CURRENT proposal content, to detect "changed since last review".
  const currentContentSnapshot = useMemo(
    () => rtSnapshot(buildRedTeamContentString(proposal?.content_json)),
    [proposal?.content_json]
  );

  // Extract gaps data from analysis or source-grounded
  const criticalGaps: CriticalGap[] = useMemo(() => {
    return analysis?.criticalGaps || gapReport?.criticalGaps || [];
  }, [analysis, gapReport]);

  const improvementOpportunities: ImprovementOpportunity[] = useMemo(() => {
    return analysis?.improvementOpportunities || gapReport?.improvementOpportunities || [];
  }, [analysis, gapReport]);

  const strengths: ProposalStrength[] = useMemo(() => {
    return analysis?.strengths || gapReport?.strengthsToHighlight || [];
  }, [analysis, gapReport]);

  // Build citations array
  const allCitations = useMemo(() => {
    return sourceMap?.allCitations?.map(c => ({
      textSpan: c.textSpan || '',
      evidenceId: c.evidenceId,
      evidenceType: c.evidenceType as any,
      evidenceSummary: c.evidenceSummary,
      isInferred: c.matchType === 'INFERRED',
      inferenceChain: c.inferenceChain,
    })) || [];
  }, [sourceMap]);

  // Content tabs configuration - matches ProposalDraftModal exactly
  const contentTabs = useMemo(() => {
    if (!proposal?.content_json) return [];

    const content = proposal.content_json;

    const sectionOrder = [
      'executive summary',
      'technical approach',
      'management approach',
      'past performance',
      'key personnel',
    ];
    const afterDeliverablesOrder = [
      'pricing & cost strategy',
      'far/dfars compliance',
    ];

    const technicalSectionsMap = new Map<string, any>();
    (content.technicalSections || []).forEach((section: any) => {
      technicalSectionsMap.set(section.title.toLowerCase(), section);
    });

    const orderedBeforeTabs: { id: string; label: string; icon: any }[] = [];
    sectionOrder.forEach(sectionName => {
      const section = technicalSectionsMap.get(sectionName);
      if (section) {
        orderedBeforeTabs.push({
          id: section.title.toLowerCase().replace(/\s+/g, '_'),
          label: section.title,
          icon: Zap,
        });
      }
    });

    const orderedAfterTabs: { id: string; label: string; icon: any }[] = [];
    afterDeliverablesOrder.forEach(sectionName => {
      const section = technicalSectionsMap.get(sectionName);
      if (section) {
        orderedAfterTabs.push({
          id: section.title.toLowerCase().replace(/\s+/g, '_'),
          label: section.title,
          icon: Zap,
        });
      }
    });

    const allOrderedNames = [...sectionOrder, ...afterDeliverablesOrder];
    const uncategorizedTabs: { id: string; label: string; icon: any }[] = [];
    (content.technicalSections || []).forEach((section: any) => {
      const lowerTitle = section.title.toLowerCase();
      if (!allOrderedNames.includes(lowerTitle)) {
        uncategorizedTabs.push({
          id: section.title.toLowerCase().replace(/\s+/g, '_'),
          label: section.title,
          icon: Zap,
        });
      }
    });

    const tabs = [
      { id: 'contractAnalysis', label: 'Contract Analysis', icon: Building2 },
      { id: 'coverLetter', label: 'Cover Letter', icon: FileText },
      ...orderedBeforeTabs,
      ...uncategorizedTabs,
      { id: 'deliverables', label: 'Deliverables', icon: Package },
      { id: 'product', label: 'Product', icon: Sparkles },
      ...orderedAfterTabs,
      { id: 'feedback', label: 'AI Feedback', icon: Zap },
    ];

    return tabs;
  }, [proposal?.content_json, isSourceGrounded, isOrchestrated]);

  // Left nav sections (top-level)
  const leftNavSections = useMemo(() => {
    const sections = [
      { id: 'content' as LeftNavSection, label: 'Proposal Content', icon: FileText },
    ];

    if (isSourceGrounded || isOrchestrated) {
      sections.push({ id: 'gaps' as LeftNavSection, label: 'Gap Report', icon: AlertTriangle });
      if (isSourceGrounded) {
        sections.push({ id: 'sources' as LeftNavSection, label: 'Source Map', icon: MapIcon });
        sections.push({ id: 'evidence' as LeftNavSection, label: 'Evidence Library', icon: Database });
      }
      sections.push({ id: 'roadmap' as LeftNavSection, label: 'Win Checklist', icon: CheckCircle });
    }

    return sections;
  }, [isSourceGrounded, isOrchestrated]);

  // Get content for a tab
  const getContent = (tabId: string): string => {
    const source = isEditMode ? editedContent : proposal?.content_json;
    if (!source) return '';

    switch (tabId) {
      case 'contractAnalysis':
        return source.contractAnalysis || '';
      case 'profileAnalysis':
        return source.profileAnalysis || '';
      case 'coverLetter':
        return source.coverLetter || '';
      case 'feedback':
        return source.feedback || '';
      default:
        const section = source.technicalSections?.find(
          (s: any) => s.title.toLowerCase().replace(/\s+/g, '_') === tabId
        );
        return section?.content || '';
    }
  };

  // --- Inline editing: apply a single field change to the active section and
  // autosave. Structured sections (JSON) are deep-set by path; plain-text
  // sections are replaced wholesale (path "__root__").
  const editedContentRef = useRef<any>(null);
  useEffect(() => { editedContentRef.current = editedContent; }, [editedContent]);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const autosaveNow = async () => {
    const latest = editedContentRef.current;
    if (!proposal?.id || !latest) return;
    try {
      await supabase
        .from('proposal_templates')
        .update({ content_json: latest, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);
      setProposal(prev => (prev ? { ...prev, content_json: latest } : prev));
      setHasUnsavedChanges(false);
    } catch (e) {
      console.error('Autosave failed:', e);
    }
  };

  const scheduleAutosave = () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { void autosaveNow(); }, 1200);
  };

  // Save the live-demo link as a top-level content_json field (format-independent of
  // the cover letter). Persisted immediately and reflected in the export draft.
  const saveDemoLink = async (rawUrl: string) => {
    const url = rawUrl.trim();
    const base = editedContentRef.current ?? editedContent ?? proposal?.content_json ?? {};
    const next = { ...base, demoLink: url || undefined };
    setEditedContent(next);
    editedContentRef.current = next;
    setEditingDemoLink(false);
    if (!proposal?.id) return;
    try {
      await supabase
        .from('proposal_templates')
        .update({ content_json: next, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);
      setProposal(prev => (prev ? { ...prev, content_json: next } : prev));
      toast.success(url ? 'Demo link saved' : 'Demo link removed');
    } catch (e) {
      console.error('Failed to save demo link:', e);
      toast.error('Could not save the demo link');
    }
  };

  const currentDemoLink: string = (editedContent?.demoLink ?? proposal?.content_json?.demoLink ?? '') as string;

  const applyToSectionString = (curStr: string, path: string, value: string): string => {
    if (path === '__root__') return value;
    let parsed: any = null;
    try { parsed = JSON.parse(curStr); } catch { parsed = null; }
    if (parsed && typeof parsed === 'object') {
      return JSON.stringify(setByPath(parsed, path, value));
    }
    return value; // plain-text section
  };

  const handleFieldChange = (path: string, value: string) => {
    setEditedContent((prev: any) => {
      const base = prev ?? proposal?.content_json ?? {};
      const next: any = { ...base };
      if (['contractAnalysis', 'profileAnalysis', 'coverLetter', 'feedback'].includes(activeTab)) {
        next[activeTab] = applyToSectionString(next[activeTab] || '', path, value);
      } else if (Array.isArray(next.technicalSections)) {
        const idx = next.technicalSections.findIndex(
          (s: any) => s.title.toLowerCase().replace(/\s+/g, '_') === activeTab
        );
        if (idx !== -1) {
          next.technicalSections = next.technicalSections.map((s: any, i: number) =>
            i === idx ? { ...s, content: applyToSectionString(s.content || '', path, value) } : s
          );
        }
      }
      editedContentRef.current = next;
      return next;
    });
    setHasUnsavedChanges(true);
    scheduleAutosave();
  };

  // Whether the active tab holds an editable structured-text section.
  const activeIsStructuredText = (() => {
    const isTextTab =
      activeTab === 'coverLetter' ||
      (proposal?.content_json?.technicalSections || []).some(
        (s: any) => s.title.toLowerCase().replace(/\s+/g, '_') === activeTab
      );
    if (!isTextTab) return false;
    try {
      const p = JSON.parse(getContent(activeTab));
      return !!p && typeof p === 'object';
    } catch {
      return false;
    }
  })();

  // Get current section title
  const getCurrentSectionTitle = () => {
    if (activeTab === 'coverLetter') return 'Cover Letter';
    const tab = contentTabs.find(t => t.id === activeTab);
    return tab?.label || activeTab;
  };

  // Handle AI Chat
  const handleSendMessage = async () => {
    if (!chatInput.trim() || chatLoading || !chatServiceRef.current) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date().toISOString() }]);
    setChatLoading(true);

    try {
      const response = await chatServiceRef.current.sendMessage(userMessage);
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.content, timestamp: new Date().toISOString() }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  // Handle gap resolution. "Reframe"/"Generate" now draft a real section edit that
  // the user must approve (no silent no-op success). "Add to Profile"/"Find Partner"
  // are navigation/advisory and handled in GapResolutionCenter.
  const handleFixGap = async (gapIndex: number, solution: string) => {
    if (!proposal?.id) return;
    if (solution !== 'reframe' && solution !== 'generate') return;

    const gap: any = criticalGaps[gapIndex];
    if (!gap) { toast.error('Could not find that gap'); return; }

    const target = resolveEditableTarget();
    if (!target) { toast.error('No editable section to apply a fix to'); return; }

    const requirement = gap.requirement || gap.currentState || 'this requirement';
    const recommendation = gap.recommendation ? `\nRecommended approach: ${gap.recommendation}` : '';
    const verb = solution === 'reframe'
      ? 'Reframe the existing content to better address'
      : 'Add focused content that addresses';
    const instruction =
      `${verb} this evaluation gap within the ${target.sectionTitle} section.\n` +
      `Requirement: ${requirement}${recommendation}\n` +
      `Integrate it naturally and keep the section coherent. Do NOT fabricate facts, ` +
      `credentials, or past performance that aren't supported by the company profile — ` +
      `if evidence is missing, write conservatively and flag what the company must supply.`;

    await proposeSectionEdit({
      sectionType: target.sectionType,
      sectionTitle: target.sectionTitle,
      instruction,
      enhancementLevel: 'moderate',
      source: 'gap',
    });
  };

  // "Fix all" can't batch-apply — every change needs the user's approval. Draft the
  // first critical gap's fix and let the user review them one at a time.
  const handleFixAllGaps = async () => {
    if (!proposal?.id) return;
    if (criticalGaps.length === 0) { toast('No critical gaps to fix'); return; }
    toast('Proposing fixes one at a time — review and apply each before the next.');
    await handleFixGap(0, 'generate');
  };

  // Open right panel with specific tool
  const openRightPanel = (tool: RightPanelTool) => {
    setRightPanelTool(tool);
    setRightPanelOpen(true);
  };

  // Save changes
  const handleSave = async () => {
    if (!proposal || !editedContent) return;

    try {
      setSaving(true);
      const { error } = await supabase
        .from('proposal_templates')
        .update({
          content_json: editedContent,
          updated_at: new Date().toISOString()
        })
        .eq('id', proposal.id);

      if (error) throw error;

      setProposal(prev => prev ? { ...prev, content_json: editedContent, updated_at: new Date().toISOString() } : null);
      setHasUnsavedChanges(false);
      setIsEditMode(false);
      toast.success('Changes saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Recompute ONLY the pricing + compliance sections from the stored generation
  // session's grounded data. generate-pricing-compliance is deterministic (no LLM),
  // so this reflects edge-function fixes without a full, credit-burning regeneration.
  const handleRecomputePricing = async () => {
    if (!proposal?.content_json || !profile?.id) return;
    const noticeId = proposal.contract?.notice_id || proposal.notice_id;
    if (!noticeId) {
      toast.error('No contract reference for this proposal');
      return;
    }
    if (recomputingPricing) return;
    setRecomputingPricing(true);
    toast.loading('Recomputing pricing from stored grounded data…', { id: 'recompute-pricing' });
    try {
      const svc = new OrchestratedProposalService(() => {});
      const result = await svc.recomputePricing(profile.id, noticeId);
      if (!result) {
        toast.error('No saved generation data for this proposal — a full regeneration is needed.', { id: 'recompute-pricing' });
        return;
      }
      // Don't write yet — build a before → after summary and let the user accept/reject.
      const oldPricing = (proposal.content_json?.technicalSections || []).find(
        (s: any) => (s.title || '').toLowerCase() === (result.pricing.title || '').toLowerCase()
      );
      const rows = buildPricingRecomputeSummary(oldPricing?.content, result.pricing.content);
      setRecomputeReview({ pricing: result.pricing, compliance: result.compliance, rows });
      toast.dismiss('recompute-pricing');
    } catch (e: any) {
      console.error('Recompute pricing failed:', e);
      toast.error(`Recompute failed: ${e?.message || 'unknown error'}`, { id: 'recompute-pricing' });
    } finally {
      setRecomputingPricing(false);
    }
  };

  // Run the adversarial Red Team submitability review on the current proposal.
  // Manual + re-runnable; persists { current, prior } and refreshes Gap Report / Win Checklist.
  const handleRunRedTeam = async () => {
    if (!proposal?.content_json || !profile?.id || !proposal?.id) return;
    const noticeId = proposal.contract?.notice_id || proposal.notice_id;
    if (!noticeId) {
      toast.error('No contract reference for this proposal');
      return;
    }
    if (runningRedTeam) return;
    setRunningRedTeam(true);
    toast.loading('Red Team is reviewing submitability…', { id: 'red-team' });
    try {
      const svc = new OrchestratedProposalService(() => {});
      const { verdict, contentJson } = await svc.runSubmitabilityReview({
        proposalTemplateId: proposal.id,
        profileId: profile.id,
        noticeId,
        contentJson: proposal.content_json,
        contract: proposal.contract,
      });
      // Reflect the persisted verdict + mapped views in the loaded proposal.
      setProposal((prev: any) => prev ? { ...prev, content_json: contentJson } : prev);
      const rec = verdict.recommendation === 'DO_NOT_SUBMIT' ? 'DO NOT SUBMIT'
        : verdict.recommendation === 'SUBMIT' ? 'SUBMIT' : 'SUBMIT WITH FIXES';
      toast.success(`Red Team: ${verdict.submitabilityScore}/100 — ${rec}`, { id: 'red-team' });
    } catch (e: any) {
      console.error('Red Team review failed:', e);
      toast.error(`Red Team review failed: ${e?.message || 'unknown error'}`, { id: 'red-team' });
    } finally {
      setRunningRedTeam(false);
    }
  };

  // Accept the recomputed pricing → swap the pricing + compliance sections into the proposal.
  const handleAcceptRecompute = async () => {
    if (!recomputeReview || !proposal?.content_json) return;
    setApplyingRecompute(true);
    try {
      const sections: any[] = Array.isArray(proposal.content_json?.technicalSections)
        ? [...proposal.content_json.technicalSections]
        : [];
      const upsert = (sec: ProposalSection) => {
        if (!sec?.title) return;
        const i = sections.findIndex((s: any) => (s.title || '').toLowerCase() === sec.title.toLowerCase());
        if (i >= 0) sections[i] = { ...sections[i], ...sec };
        else sections.push(sec);
      };
      upsert(recomputeReview.pricing);
      upsert(recomputeReview.compliance);
      const latest = { ...proposal.content_json, technicalSections: sections };

      const { error } = await supabase
        .from('proposal_templates')
        .update({ content_json: latest, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);
      if (error) throw error;

      setProposal(prev => (prev ? { ...prev, content_json: latest } : prev));
      setEditedContent(latest);
      setRecomputeReview(null);
      toast.success('Recomputed pricing applied to the proposal.');
    } catch (e: any) {
      console.error('Apply recomputed pricing failed:', e);
      toast.error(`Failed to apply: ${e?.message || 'unknown error'}`);
    } finally {
      setApplyingRecompute(false);
    }
  };

  // Reject the recomputed pricing → discard, leave the proposal untouched.
  const handleRejectRecompute = () => {
    setRecomputeReview(null);
    toast('Recomputed pricing discarded — proposal unchanged.', { icon: '↩️' });
  };

  // Export proposal
  const handleExport = async (format: 'pdf' | 'word' | 'markdown', includeCitations: boolean = true) => {
    if (!proposal?.contract || !proposal?.content_json) {
      toast.error('Cannot export: Missing proposal or contract data');
      return;
    }

    try {
      setExporting(true);
      setShowExportMenu(false);

      const exportDraft = includeCitations ? proposal.content_json : stripCitations(proposal.content_json);

      switch (format) {
        case 'pdf':
          await ProposalExportService.exportToPDF(exportDraft, proposal.contract);
          break;
        case 'word':
          if (isOrchestrated || isSourceGrounded) {
            await BeautifulDocxExporter.export(exportDraft, proposal.contract);
          } else {
            await ProposalExportService.exportToWord(exportDraft, proposal.contract);
          }
          break;
        case 'markdown':
          await ProposalExportService.exportToMarkdown(exportDraft, proposal.contract);
          break;
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const stripCitations = (content: any): any => {
    const stripText = (text: string) =>
      text?.replace(/\s*\[[A-Z]+-\d+(?:,\s*[^\]]+)?]\s*/gi, ' ').replace(/\s+/g, ' ') || '';

    return {
      ...content,
      coverLetter: stripText(content.coverLetter),
      technicalSections: content.technicalSections?.map((s: any) => ({
        ...s,
        content: stripText(s.content),
      })),
      feedback: stripText(content.feedback),
      contractAnalysis: stripText(content.contractAnalysis),
      profileAnalysis: stripText(content.profileAnalysis),
    };
  };

  // Open feedback dialog for section regeneration
  const handleOpenFeedbackDialog = (sectionType: string, sectionTitle: string) => {
    setRegeneratingSectionType(sectionType);
    setRegeneratingSectionTitle(sectionTitle);
    setShowFeedbackDialog(true);
  };

  // Regenerate a section's content via the async edge job and return the new JSON
  // string. Does NOT persist anything — callers decide what to do with the result.
  const regenerateSectionContent = async (
    sectionType: string,
    sectionTitle: string,
    userFeedback: string,
    enhancementLevel: 'minor' | 'moderate' | 'significant' = 'moderate'
  ): Promise<string> => {
    if (!proposal?.contract) throw new Error('No contract data for this proposal');
    const currentContent = getContent(sectionType);
    const originalContext = {
      company: {
        name: profile?.company_name || null,
        valueProposition: profile?.value_proposition || null,
        capabilities: profile?.capabilities?.map((c: any) => c.name) || [],
      },
      contract: {
        title: proposal.contract.title,
        agency: proposal.contract.agency,
        naicsCode: proposal.contract.naics_code || null,
      },
    };

    // Async job + poll: big sections can't finish inside the 150s sync gateway wall.
    const jobId =
      (globalThis.crypto?.randomUUID?.() as string) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const { error: invokeError } = await supabase.functions.invoke('regenerate-section', {
      body: {
        sectionType,
        currentContent,
        userFeedback,
        originalContext,
        enhancementLevel,
        job_id: jobId,
        profile_id: profile?.id,
        proposal_id: proposal.id,
      },
    });
    if (invokeError) throw invokeError;

    let data: { content: string } | null = null;
    const startedAt = Date.now();
    const TIMEOUT_MS = 6 * 60 * 1000;
    while (Date.now() - startedAt < TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, 3000));
      const { data: job, error: pollError } = await supabase
        .from('section_regeneration_jobs')
        .select('status, result, error')
        .eq('id', jobId)
        .maybeSingle();
      if (pollError) continue;
      if (!job) continue;
      if (job.status === 'error') throw new Error(job.error || 'Regeneration failed');
      if (job.status === 'done') { data = job.result as { content: string }; break; }
    }
    if (!data) throw new Error('Regeneration timed out — please try again');
    return data.content;
  };

  // Build the next content_json with a section's content replaced (cover letter or a
  // technical section). Pure — does not persist.
  const writeSectionContent = (sectionType: string, sectionTitle: string, newContentStr: string): any => {
    const base = editedContentRef.current ?? editedContent ?? proposal?.content_json ?? {};
    const next: any = { ...base };
    if (sectionType === 'coverLetter' || sectionType === 'cover_letter') {
      next.coverLetter = newContentStr;
    } else if (Array.isArray(next.technicalSections)) {
      const idx = next.technicalSections.findIndex(
        (s: any) => s.title.toLowerCase().replace(/\s+/g, '_') === sectionType || s.title === sectionTitle
      );
      if (idx !== -1) {
        next.technicalSections = next.technicalSections.map((s: any, i: number) =>
          i === idx ? { ...s, content: newContentStr } : s
        );
      }
    }
    return next;
  };

  // Pick the section an AI fix should target: the active section if it's editable,
  // else Technical Approach, else the first technical section, else the cover letter.
  const resolveEditableTarget = (): { sectionType: string; sectionTitle: string } | null => {
    if (activeTab === 'coverLetter') return { sectionType: 'coverLetter', sectionTitle: 'Cover Letter' };
    const sections = proposal?.content_json?.technicalSections || [];
    const activeMatch = sections.find((s: any) => s.title.toLowerCase().replace(/\s+/g, '_') === activeTab);
    if (activeMatch) return { sectionType: activeTab, sectionTitle: activeMatch.title };
    const tech = sections.find((s: any) => /technical/i.test(s.title)) || sections[0];
    if (tech) return { sectionType: tech.title.toLowerCase().replace(/\s+/g, '_'), sectionTitle: tech.title };
    if (proposal?.content_json?.coverLetter != null) return { sectionType: 'coverLetter', sectionTitle: 'Cover Letter' };
    return null;
  };

  // Is the currently-viewed section one the AI can edit (cover letter / a tech section)?
  const activeSectionEditable = (() => {
    if (activeTab === 'coverLetter') return true;
    return (proposal?.content_json?.technicalSections || []).some(
      (s: any) => s.title.toLowerCase().replace(/\s+/g, '_') === activeTab
    );
  })();

  // Draft an AI change for a section and open the approval dialog. NOTHING is saved
  // here — the user reviews the proposed text and explicitly Applies (or Discards).
  const proposeSectionEdit = async (opts: {
    sectionType: string;
    sectionTitle: string;
    instruction: string;
    enhancementLevel?: 'minor' | 'moderate' | 'significant';
    source: 'chat' | 'gap';
    coverageItemId?: string;
  }) => {
    if (aiEdit) { toast.error('Finish reviewing the current proposed change first'); return; }
    setAiEdit({
      status: 'drafting',
      sectionType: opts.sectionType,
      sectionTitle: opts.sectionTitle,
      instruction: opts.instruction,
      source: opts.source,
      coverageItemId: opts.coverageItemId,
    });
    try {
      const before = getContent(opts.sectionType);
      const after = await regenerateSectionContent(
        opts.sectionType, opts.sectionTitle, opts.instruction, opts.enhancementLevel || 'moderate'
      );
      setAiEdit({
        status: 'review',
        sectionType: opts.sectionType,
        sectionTitle: opts.sectionTitle,
        instruction: opts.instruction,
        source: opts.source,
        coverageItemId: opts.coverageItemId,
        before,
        after,
      });
    } catch (error: any) {
      console.error('Propose-edit error:', error);
      toast.error(`Couldn't draft change: ${error.message || error}`);
      setAiEdit(null);
    }
  };

  // User approved the proposed edit → persist it.
  const applyPendingEdit = async () => {
    if (!aiEdit || aiEdit.status !== 'review' || aiEdit.after == null || !proposal?.id) return;
    setApplyingAiEdit(true);
    try {
      const next = writeSectionContent(aiEdit.sectionType, aiEdit.sectionTitle, aiEdit.after);
      setEditedContent(next);
      editedContentRef.current = next;
      await supabase
        .from('proposal_templates')
        .update({ content_json: next, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);
      setProposal(prev => (prev ? { ...prev, content_json: next } : prev));
      setHasUnsavedChanges(false);
      toast.success(`Applied change to ${aiEdit.sectionTitle}`);
      const coverageItemId = aiEdit.coverageItemId;
      setAiEdit(null);
      // If this fix came from a Coverage requirement, re-check just that one row so
      // the user immediately sees it flip to covered (cheap — no full matrix re-run).
      if (coverageItemId) void recheckCoverageItem(coverageItemId);
    } catch (error: any) {
      console.error('Apply-edit error:', error);
      toast.error('Failed to apply the change');
    } finally {
      setApplyingAiEdit(false);
    }
  };

  const discardPendingEdit = () => {
    if (applyingAiEdit) return;
    setAiEdit(null);
  };

  // Deterministic placeholder fix: scan all sections, fill from profile + contract,
  // preview for approval. No LLM — instant and free.
  const handleFixPlaceholders = () => {
    if (!proposal?.content_json) return;
    const resolve = buildPlaceholderResolver(profile, proposal.contract);
    const scan = resolveProposalPlaceholders(proposal.content_json, resolve);
    if (scan.filled.length === 0 && scan.unresolved.length === 0) {
      toast('No placeholders found in the proposal');
      return;
    }
    setPlaceholderScan(scan);
  };

  const applyPlaceholders = async () => {
    if (!placeholderScan || !proposal?.id) return;
    setApplyingPlaceholders(true);
    try {
      let next = placeholderScan.updatedContentJson;
      // Prune resolved "placeholder" critical gaps so fixed items actually disappear.
      const stillHas = sectionsWithPlaceholders(next);
      const gr = next.gapReport;
      if (gr && Array.isArray(gr.criticalGaps)) {
        const prunedCritical = gr.criticalGaps.filter((g: any) => {
          const req = String(g?.requirement || '');
          if (!/placeholder/i.test(req)) return true;        // keep non-placeholder gaps
          const m = req.match(/in\s+(.+?)\s*$/i);
          const sec = m ? m[1].trim() : '';
          return sec ? stillHas.has(sec) : stillHas.size > 0; // drop if that section is now clean
        });
        next = { ...next, gapReport: { ...gr, criticalGaps: prunedCritical } };
      }
      setEditedContent(next);
      editedContentRef.current = next;
      await supabase
        .from('proposal_templates')
        .update({ content_json: next, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);
      setProposal(prev => (prev ? { ...prev, content_json: next } : prev));
      const filledCount = placeholderScan.filled.length;
      const left = placeholderScan.unresolved.length;
      toast.success(`Filled ${filledCount} placeholder${filledCount === 1 ? '' : 's'}${left ? ` · ${left} still need your input` : ''}`);
      setPlaceholderScan(null);
    } catch (e: any) {
      console.error('Apply placeholders error:', e);
      toast.error('Failed to fill placeholders');
    } finally {
      setApplyingPlaceholders(false);
    }
  };

  // Requirement coverage: run the matrix check (read-only) and persist it.
  const handleCheckCoverage = async () => {
    if (!proposal?.id || !profile?.id || coverageLoading) return;
    const noticeId = proposal.notice_id || proposal.contract?.noticeId || proposal.contract?.notice_id;
    if (!noticeId) { toast.error('No contract reference for this proposal'); return; }
    setCoverageLoading(true);
    toast.loading('Checking requirement coverage…', { id: 'coverage' });
    try {
      const svc = new OrchestratedProposalService(() => {});
      const { coverage: cov } = await svc.checkRequirementCoverage({
        proposalTemplateId: proposal.id,
        profileId: profile.id,
        noticeId,
        contentJson: proposal.content_json,
        contract: proposal.contract,
      });
      setCoverage(cov);
      setProposal(prev => (prev ? { ...prev, content_json: { ...prev.content_json, requirementCoverage: cov } } : prev));
      toast.success(`Coverage: ${cov.summary.covered}/${cov.summary.total} covered, ${cov.summary.uncovered} uncovered`, { id: 'coverage' });
    } catch (e: any) {
      console.error('Coverage check error:', e);
      toast.error(`Coverage check failed: ${e.message || e}`, { id: 'coverage' });
    } finally {
      setCoverageLoading(false);
    }
  };

  // "Address" a requirement → draft an approval-gated edit on the matched (or most
  // relevant) section, with the requirement as the instruction.
  const handleAddressRequirement = async (item: RequirementCoverageItem) => {
    let target: { sectionType: string; sectionTitle: string } | null = null;
    if (item.section) {
      if (/cover letter/i.test(item.section)) target = { sectionType: 'coverLetter', sectionTitle: 'Cover Letter' };
      else {
        const match = (proposal?.content_json?.technicalSections || []).find(
          (s: any) => s.title.toLowerCase() === item.section!.toLowerCase()
        );
        if (match) target = { sectionType: match.title.toLowerCase().replace(/\s+/g, '_'), sectionTitle: match.title };
      }
    }
    if (!target) target = resolveEditableTarget();
    if (!target) { toast.error('No editable section to address this in'); return; }

    const instruction =
      `Ensure the ${target.sectionTitle} section fully and specifically addresses this solicitation requirement.\n` +
      `Requirement (${item.source}): ${item.requirement}\n` +
      (item.status === 'partial' && item.evidence ? `Currently only partial — gap: ${item.evidence}\n` : '') +
      `Integrate it naturally and keep the section coherent. Do NOT fabricate facts, credentials, or past ` +
      `performance not supported by the company profile; if evidence is missing, write conservatively and flag it.`;

    await proposeSectionEdit({
      sectionType: target.sectionType,
      sectionTitle: target.sectionTitle,
      instruction,
      enhancementLevel: 'moderate',
      source: 'gap',
      coverageItemId: item.id,
    });
  };

  // Re-check ONE coverage requirement against the current proposal and update its
  // row live (cheap — a single requirement through the same edge fn, no full re-run).
  const recheckCoverageItem = async (itemId: string) => {
    if (!coverage || !proposal?.id) return;
    const item = coverage.items.find((i) => i.id === itemId);
    if (!item) return;
    try {
      const ts: any[] = Array.isArray(proposal.content_json?.technicalSections) ? proposal.content_json.technicalSections : [];
      const sections = [
        { title: 'Cover Letter', text: proposal.content_json?.coverLetter || '' },
        ...ts.map((s: any) => ({ title: s.title, text: s.content || '' })),
      ].filter((s) => (s.text || '').trim());
      const { data } = await supabase.functions.invoke('check-requirement-coverage', {
        body: { sections, solicitation: { complianceRequirements: [item.requirement] } },
      });
      const re = data?.coverage?.items?.[0];
      if (!re) return;
      // Keep the original requirement/source/category; take the fresh assessment.
      const merged = { ...item, status: re.status, section: re.section ?? item.section, evidence: re.evidence || item.evidence, confidence: re.confidence || item.confidence };
      const nextItems = coverage.items.map((i) => (i.id === itemId ? merged : i));
      const contentItems = nextItems.filter((x) => x.kind !== 'acknowledgement');
      const nextSummary = {
        ...coverage.summary,
        total: contentItems.length,
        covered: contentItems.filter((x) => x.status === 'covered').length,
        partial: contentItems.filter((x) => x.status === 'partial').length,
        uncovered: contentItems.filter((x) => x.status === 'uncovered').length,
      };
      const nextCoverage = { ...coverage, items: nextItems, summary: nextSummary };
      const nextContent = { ...(editedContentRef.current ?? proposal.content_json), requirementCoverage: nextCoverage };
      setCoverage(nextCoverage);
      setProposal(prev => (prev ? { ...prev, content_json: nextContent } : prev));
      editedContentRef.current = nextContent;
      await supabase.from('proposal_templates').update({ content_json: nextContent, updated_at: new Date().toISOString() }).eq('id', proposal.id);
      if (merged.status === 'covered') toast.success('Requirement now covered ✓');
      else toast(`Re-checked: still ${merged.status}`);
    } catch (e) {
      console.error('Coverage re-check error:', e);
    }
  };

  // Mark acknowledgement-type requirements as acknowledged (agree to comply). No LLM;
  // persists onto content_json.requirementCoverage. ids=null acknowledges all open ones.
  const acknowledgeRequirements = async (ids: string[] | null) => {
    if (!coverage || !proposal?.id) return;
    const idSet = ids ? new Set(ids) : null;
    const nextItems = coverage.items.map((it) =>
      it.kind === 'acknowledgement' && (!idSet || idSet.has(it.id)) ? { ...it, acknowledged: true } : it
    );
    const nextCoverage = { ...coverage, items: nextItems };
    const nextContent = { ...proposal.content_json, requirementCoverage: nextCoverage };
    setCoverage(nextCoverage);
    setProposal(prev => (prev ? { ...prev, content_json: nextContent } : prev));
    try {
      await supabase
        .from('proposal_templates')
        .update({ content_json: nextContent, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);
      toast.success(ids ? 'Acknowledged' : 'All acknowledgements recorded');
    } catch (e) {
      console.error('Acknowledge persist error:', e);
      toast.error('Could not save acknowledgement');
    }
  };

  // Handle section regeneration (Enhance flow — explicit user action via its dialog).
  const handleRegenerateSection = async (feedback: SectionFeedback) => {
    if (!regeneratingSectionType || !proposal?.contract) return;
    // Guard against duplicate in-flight submissions (rapid double-clicks).
    if (regenerating) return;

    setRegenerating(true);
    try {
      const newContentStr = await regenerateSectionContent(
        feedback.sectionType,
        regeneratingSectionTitle,
        feedback.userFeedback,
        feedback.enhancementLevel
      );
      const next = writeSectionContent(feedback.sectionType, regeneratingSectionTitle, newContentStr);
      setEditedContent(next);
      editedContentRef.current = next;
      await supabase
        .from('proposal_templates')
        .update({ content_json: next, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);
      setProposal(prev => (prev ? { ...prev, content_json: next } : prev));
      setHasUnsavedChanges(false);
      toast.success(`${regeneratingSectionTitle} regenerated successfully!`);
      setShowFeedbackDialog(false);
    } catch (error: any) {
      console.error('Section regeneration error:', error);
      toast.error(`Failed to regenerate: ${error.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  // Navigate to profile section
  const handleNavigateToProfile = (section: string) => {
    navigate(`/profile#${section}`);
  };

  // Deliverables handlers
  const handleRegenerateDeliverables = async () => {
    if (!proposal?.contract || !proposal?.id || !profile?.id) return;
    const noticeId = proposal.notice_id || proposal.contract.noticeId || proposal.contract.notice_id;
    if (!noticeId) {
      toast.error('No contract reference for this proposal');
      return;
    }

    setRegeneratingDeliverables(true);
    toast.loading('Regenerating deliverables from the solicitation…', { id: 'regen-deliv' });

    try {
      // Route through the REAL pipeline: structure-price-schedule → schedule-primary
      // extraction → enrich → labor framework, rebuilt from the saved generation session.
      const svc = new OrchestratedProposalService(() => {});
      const laborData = await svc.regenerateDeliverablesForProposal({ profileId: profile.id, noticeId });

      if (!laborData || !(laborData as any)?.deliverables_with_labor?.length) {
        toast.error('No saved generation data for this proposal — run a full generation first so the solicitation is analyzed.', { id: 'regen-deliv' });
        return;
      }

      const newContent = { ...(proposal.content_json || {}), deliverablesData: laborData };
      setEditedContent(newContent);
      setProposal((prev: any) => prev ? { ...prev, content_json: newContent } : prev);
      await supabase
        .from('proposal_templates')
        .update({ content_json: newContent, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);

      const n = (laborData as any).deliverables_with_labor.length;
      toast.success(`Regenerated ${n} deliverables from the solicitation.`, { id: 'regen-deliv' });
    } catch (error: any) {
      console.error('Deliverables regeneration error:', error);
      toast.error(`Failed to regenerate: ${error.message}`, { id: 'regen-deliv' });
    } finally {
      setRegeneratingDeliverables(false);
    }
  };

  // Persist inline edits to the structured deliverables section.
  const handleSaveDeliverables = async (updated: any) => {
    if (!proposal?.id) return;
    setSavingDeliverables(true);
    try {
      const newContent = { ...(proposal.content_json || {}), deliverablesData: updated };
      setEditedContent(newContent);
      setProposal((prev: any) => prev ? { ...prev, content_json: newContent } : prev);
      const { error } = await supabase
        .from('proposal_templates')
        .update({ content_json: newContent, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);
      if (error) throw error;
      toast.success('Deliverables saved');
    } catch (e: any) {
      console.error('Save deliverables failed:', e);
      toast.error(`Failed to save: ${e?.message || 'unknown error'}`);
    } finally {
      setSavingDeliverables(false);
    }
  };

  // Synthesize product from existing deliverables
  const handleSynthesizeProduct = async () => {
    if (!proposal?.contract || !editedContent?.deliverablesData) {
      toast.error('Generate deliverables first before synthesizing product');
      return;
    }

    const contractId = proposal.contract.noticeId || proposal.contract.id;
    setRegeneratingDeliverables(true);

    try {
      console.log('✨ Synthesizing product definition...');

      // Map deliverables_with_labor to the format expected by synthesize-product
      const deliverables = editedContent.deliverablesData.deliverables_with_labor.map((d: any) => ({
        id: d.deliverable_id || d.id,
        name: d.deliverable_name || d.name,
        description: d.description || '',
        type: d.type || 'service',
        category: d.category,
        quantity: d.quantity || 1,
        unit: d.unit || 'each',
        specifications: d.specifications,
        labor_phases: d.labor_phases,
        total_hours: d.total_hours,
        compliance_requirements: d.compliance_requirements,
      }));

      // Map compliance framework to compliance_requirements if available
      const compliance_requirements = editedContent.deliverablesData.compliance_framework?.requirements?.map((r: any) => ({
        id: r.id || r.requirement_id,
        name: r.name || r.requirement,
        category: r.category || 'general',
        description: r.description || r.details || '',
      })) || [];

      const { data: productData, error: productError } = await supabase.functions.invoke('synthesize-product', {
        body: {
          deliverables,
          compliance_requirements,
          labor_summary: editedContent.deliverablesData.labor_summary,
          contract_metadata: {
            notice_id: contractId,
            title: proposal.contract.title,
            contract_type: proposal.contract.type || 'service',
            naics_code: proposal.contract.naicsCode || proposal.contract.naics_code || '',
            agency: proposal.contract.agency,
            description: proposal.contract.description,
          },
        },
      });

      if (productError) throw productError;

      if (!productData?.product_definition) {
        throw new Error('No product definition returned from synthesis');
      }

      console.log('✅ Product synthesis complete');

      // Update deliverablesData with product_definition
      const newDeliverablesData = {
        ...editedContent.deliverablesData,
        product_definition: productData.product_definition,
      };

      const newContent = {
        ...editedContent,
        deliverablesData: newDeliverablesData,
      };

      setEditedContent(newContent);

      // Save to database
      await supabase
        .from('proposal_templates')
        .update({ content_json: newContent, updated_at: new Date().toISOString() })
        .eq('id', proposal.id);

      toast.success('Product synthesis complete!');

    } catch (error: any) {
      console.error('❌ Product synthesis error:', error);
      toast.error(`Failed to synthesize product: ${error.message}`);
    } finally {
      setRegeneratingDeliverables(false);
    }
  };

  // Handle deliverables section enhancement with user feedback
  const handleEnhanceDeliverablesSection = async (feedback: DeliverableFeedback) => {
    if (!proposal?.contract || !editedContent?.deliverablesData) return;

    setEnhancingDeliverableSection(true);
    console.log('🚀 Starting deliverables section enhancement:', feedback);

    try {
      const { data, error } = await supabase.functions.invoke('enhance-deliverables-section', {
        body: {
          deliverables_data: {
            deliverables_with_labor: editedContent.deliverablesData.deliverables_with_labor,
            labor_summary: editedContent.deliverablesData.labor_summary,
            pricing_inputs: editedContent.deliverablesData.pricing_inputs,
            compliance_framework: editedContent.deliverablesData.compliance_framework,
            timeline_summary: editedContent.deliverablesData.timeline_summary,
          },
          user_feedback: feedback.userFeedback,
          enhancement_level: feedback.enhancementLevel,
          focus_areas: feedback.focusAreas,
          user_profile: {
            company_name: profile?.company_name,
            capabilities: profile?.capabilities?.map((c: any) => c.name),
          },
          rfq_context: {
            title: proposal.contract.title,
            agency: proposal.contract.agency,
            naics_code: proposal.contract.naicsCode || proposal.contract.naics_code,
          },
          // Include RFQ documents/attachments for context
          rfq_documents: proposal.contract.attachments || [],
        },
      });

      if (error) throw error;

      if (data?.success && Array.isArray(data?.enhanced_deliverables)) {
        // MERGE by id — never wholesale-replace. enhance-deliverables-section's LLM can
        // return a subset (truncation), which previously collapsed the whole list (49→1).
        // Apply enhancements onto the existing deliverables by deliverable_id; keep every
        // original entry so Enhance can only refine, never delete.
        const existing = editedContent.deliverablesData.deliverables_with_labor || [];
        const enhancedById = new Map(
          (data.enhanced_deliverables as any[])
            .filter((d) => d && d.deliverable_id != null)
            .map((d) => [d.deliverable_id, d])
        );
        const merged = existing.map((d: any) =>
          enhancedById.has(d.deliverable_id) ? { ...d, ...enhancedById.get(d.deliverable_id) } : d
        );
        const appliedCount = existing.filter((d: any) => enhancedById.has(d.deliverable_id)).length;

        const newDeliverablesData = {
          ...editedContent.deliverablesData,
          deliverables_with_labor: merged,
          // Only accept the edge's recomputed labor summary if the enhancement covered the
          // FULL list; a subset response would otherwise shrink the totals. Otherwise keep ours.
          labor_summary: ((data.enhanced_deliverables as any[]).length >= existing.length && data.updated_labor_summary)
            ? data.updated_labor_summary
            : editedContent.deliverablesData.labor_summary,
        };

        const newContent = { ...editedContent, deliverablesData: newDeliverablesData };
        setEditedContent(newContent);

        await supabase
          .from('proposal_templates')
          .update({ content_json: newContent, updated_at: new Date().toISOString() })
          .eq('id', proposal.id);

        toast.success(`Enhanced ${appliedCount} of ${merged.length} deliverables`);
        setShowDeliverableFeedbackDialog(false);
      }
    } catch (error: any) {
      console.error('❌ Deliverables section enhancement error:', error);
      toast.error(`Failed to enhance deliverables: ${error.message}`);
    } finally {
      setEnhancingDeliverableSection(false);
    }
  };

  // Handle individual deliverable enhancement with user feedback
  const handleEnhanceIndividualDeliverable = async (feedback: DeliverableFeedback) => {
    if (!proposal?.contract || !editedContent?.deliverablesData || !feedback.deliverableId) return;

    setEnhancingDeliverable(true);
    setEnhancingDeliverableId(feedback.deliverableId);

    try {
      const currentDeliverable = editedContent.deliverablesData.deliverables_with_labor.find(
        (d: any) => d.deliverable_id === feedback.deliverableId
      );

      if (!currentDeliverable) {
        throw new Error('Deliverable not found');
      }

      const { data, error } = await supabase.functions.invoke('enhance-deliverable-section', {
        body: {
          deliverable_id: feedback.deliverableId,
          current_content: {
            deliverable_name: currentDeliverable.deliverable_name,
            description: currentDeliverable.description,
            type: currentDeliverable.type,
            labor_phases: currentDeliverable.labor_phases,
            total_hours: currentDeliverable.total_hours,
            specifications: currentDeliverable.specifications,
            compliance_requirements: currentDeliverable.compliance_requirements,
          },
          user_profile: {
            company_name: profile?.company_name,
            capabilities: profile?.capabilities?.map((c: any) => c.name),
          },
          rfq_context: {
            title: proposal.contract.title,
            agency: proposal.contract.agency,
          },
          enhancement_type: feedback.focusAreas.length === 1 ? feedback.focusAreas[0] : 'all',
          user_feedback: feedback.userFeedback,
          // Include RFQ documents/attachments for context
          rfq_documents: proposal.contract.attachments || [],
        },
      });

      if (error) throw error;

      if (data?.success && data?.enhanced_deliverable) {
        // Update the deliverable in the local state
        const newDeliverablesData = {
          ...editedContent.deliverablesData,
          deliverables_with_labor: editedContent.deliverablesData.deliverables_with_labor.map((d: any) =>
            d.deliverable_id === feedback.deliverableId
              ? { ...d, ...data.enhanced_deliverable }
              : d
          ),
        };

        const newContent = {
          ...editedContent,
          deliverablesData: newDeliverablesData,
        };

        setEditedContent(newContent);

        // Save to database
        await supabase
          .from('proposal_templates')
          .update({ content_json: newContent, updated_at: new Date().toISOString() })
          .eq('id', proposal.id);

        toast.success(`Enhanced ${currentDeliverable.deliverable_name}`);
        setShowDeliverableFeedbackDialog(false);
        setSelectedDeliverableForEnhancement(null);
      }
    } catch (error: any) {
      console.error('❌ Individual deliverable enhancement error:', error);
      toast.error(`Failed to enhance deliverable: ${error.message}`);
    } finally {
      setEnhancingDeliverable(false);
      setEnhancingDeliverableId(null);
    }
  };

  // Handle product section enhancement with user feedback
  const handleEnhanceProductSection = async (feedback: ProductFeedback) => {
    if (!proposal?.contract || !editedContent?.deliverablesData?.product_definition) return;

    setEnhancingProductSection(true);
    console.log('🚀 Starting product section enhancement:', feedback);

    try {
      const { data, error } = await supabase.functions.invoke('enhance-product-section', {
        body: {
          product_definition: editedContent.deliverablesData.product_definition,
          user_feedback: feedback.userFeedback,
          enhancement_level: feedback.enhancementLevel,
          focus_areas: feedback.focusAreas,
          deliverables_context: editedContent.deliverablesData.deliverables_with_labor?.slice(0, 5),
          user_profile: {
            company_name: profile?.company_name,
            capabilities: profile?.capabilities?.map((c: any) => c.name),
          },
          rfq_context: {
            title: proposal.contract.title,
            agency: proposal.contract.agency,
            naics_code: proposal.contract.naicsCode || proposal.contract.naics_code,
          },
          // Include RFQ documents/attachments for context
          rfq_documents: proposal.contract.attachments || [],
        },
      });

      if (error) throw error;

      if (data?.success && data?.enhanced_product) {
        // Update product definition
        const newDeliverablesData = {
          ...editedContent.deliverablesData,
          product_definition: data.enhanced_product,
        };

        const newContent = {
          ...editedContent,
          deliverablesData: newDeliverablesData,
        };

        setEditedContent(newContent);

        // Save to database
        await supabase
          .from('proposal_templates')
          .update({ content_json: newContent, updated_at: new Date().toISOString() })
          .eq('id', proposal.id);

        toast.success('Product definition enhanced successfully');
        setShowProductFeedbackDialog(false);
      }
    } catch (error: any) {
      console.error('❌ Product section enhancement error:', error);
      toast.error(`Failed to enhance product: ${error.message}`);
    } finally {
      setEnhancingProductSection(false);
    }
  };

  // Handle opening deliverable feedback dialog for section or individual
  const handleOpenDeliverableFeedback = (mode: 'section' | 'individual', deliverableId?: string, deliverableName?: string) => {
    setDeliverableFeedbackMode(mode);
    if (mode === 'individual' && deliverableId && deliverableName) {
      setSelectedDeliverableForEnhancement({ id: deliverableId, name: deliverableName });
    } else {
      setSelectedDeliverableForEnhancement(null);
    }
    setShowDeliverableFeedbackDialog(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bw-surface-subtle">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-bw-primary mx-auto mb-4" />
          <p className="text-bw-muted">Loading proposal...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bw-surface-subtle">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-urgent mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-bw-ink mb-2">Proposal not found</h2>
          <button
            onClick={() => navigate('/workspace')}
            className="text-bw-primary hover:underline"
          >
            Return to Workspace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col bg-bw-app -mx-4 sm:-mx-6 lg:-mx-8 overflow-hidden">
      {/* ===== TOP HEADER WITH NAVIGATION ===== */}
      <div className="bg-bw-surface border-b border-bw-border text-bw-ink px-3 sm:px-4 py-2.5 flex items-center justify-between gap-2 flex-shrink-0">
        <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
          {/* Back button */}
          <button
            onClick={() => navigate('/workspace')}
            className="p-2 text-bw-muted hover:bg-bw-surface-subtle rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          {/* Section Navigation Dropdown */}
          <div className="relative min-w-0" ref={sectionDropdownRef}>
            <button
              onClick={() => setShowSectionDropdown(!showSectionDropdown)}
              className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 bg-bw-surface-subtle hover:bg-bw-border/60 text-bw-ink rounded-lg transition-colors min-w-0 max-w-full"
            >
              <FileText className="h-4 w-4 text-bw-primary flex-shrink-0" />
              <span className="text-sm font-semibold max-w-[96px] sm:max-w-[160px] lg:max-w-[220px] truncate">
                {activeSection === 'content' ? getCurrentSectionTitle() :
                 leftNavSections.find(s => s.id === activeSection)?.label || 'Select Section'}
              </span>
              <ChevronDown className={`h-4 w-4 text-bw-muted flex-shrink-0 transition-transform ${showSectionDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showSectionDropdown && (
              <div className="absolute left-0 top-full mt-2 w-64 bg-bw-surface rounded-lg shadow-panel border border-bw-border z-50 max-h-[70vh] overflow-y-auto">
                {/* Top-level sections (Views) */}
                <div className="p-2 border-b border-bw-border">
                  <p className="px-3 py-1 text-[10px] font-bold text-bw-muted uppercase tracking-wider font-mono">Views</p>
                  {leftNavSections.map(section => (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveSection(section.id);
                        setShowSectionDropdown(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        activeSection === section.id
                          ? 'bg-bw-primary text-white font-semibold'
                          : 'text-bw-ink hover:bg-bw-surface-subtle'
                      }`}
                    >
                      <section.icon className="h-4 w-4" />
                      {section.label}
                    </button>
                  ))}
                </div>

                {/* Content tabs when viewing Proposal Content */}
                {activeSection === 'content' && (
                  <div className="p-2">
                    <p className="px-3 py-1 text-[10px] font-bold text-bw-muted uppercase tracking-wider font-mono">Sections</p>
                    {contentTabs.map((tab) => {
                      const Icon = tab.icon;
                      const filled = !!String(getContent(tab.id) || '').trim();
                      const isActive = activeTab === tab.id;
                      // Completion dot: active = blue, has content = green, empty = hollow.
                      const dotCls = isActive
                        ? 'bg-bw-primary border-bw-primary'
                        : filled
                          ? 'bg-success border-success'
                          : 'bg-transparent border-bw-border';
                      return (
                        <button
                          key={tab.id}
                          onClick={() => {
                            setActiveTab(tab.id);
                            setShowSectionDropdown(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                            isActive
                              ? 'bg-bw-primary-tint text-bw-primary font-semibold'
                              : 'text-bw-ink hover:bg-bw-surface-subtle'
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full border-[1.5px] flex-shrink-0 ${dotCls}`} />
                          <Icon className="h-4 w-4" />
                          {tab.label}
                        </button>
                      );
                    })}
                    {(() => {
                      const done = contentTabs.filter(t => String(getContent(t.id) || '').trim()).length;
                      const pct = contentTabs.length ? Math.round((done / contentTabs.length) * 100) : 0;
                      return (
                        <div className="mx-1 mt-2 p-2.5 bg-bw-surface-subtle rounded-lg">
                          <div className="flex items-center justify-between text-[11px] font-semibold text-bw-ink mb-1.5">
                            <span>Progress</span>
                            <span className="font-mono">{done} / {contentTabs.length} sections</span>
                          </div>
                          <div className="w-full h-1 bg-fit-track rounded-full overflow-hidden">
                            <div className="h-full bg-bw-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Title (smaller, moved after dropdown) */}
          <div className="min-w-0 hidden md:block">
            <h1 className="text-sm font-medium truncate text-bw-muted">
              {proposal.contract?.title || proposal.template_name}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {/* AI Score Badge */}
            {analysis && (
              <div className={`hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold font-mono border ${
                analysis.status === 'ready' ? 'bg-success-bg text-success border-success-border' :
                analysis.status === 'needs-attention' ? 'bg-fit-moderate-bg text-fit-moderate border-fit-moderate-border' :
                analysis.status === 'critical' ? 'bg-urgent-bg text-urgent border-urgent-border' :
                'bg-bw-primary-tint text-bw-primary border-bw-primary/20'
              }`}>
                <TrendingUp className="h-3 w-3" />
                {analysis.overallScore}%
              </div>
            )}

            {/* View/Edit Toggle — primary control, always visible */}
            <button
              onClick={() => setIsEditMode(!isEditMode)}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                isEditMode ? 'bg-fit-moderate-bg text-fit-moderate border border-fit-moderate-border' : 'bg-bw-surface-subtle text-bw-ink hover:bg-bw-border/60'
              }`}
            >
              {isEditMode ? <Edit2 className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              <span className="hidden lg:inline">{isEditMode ? 'Editing' : 'View'}</span>
            </button>

            {/* More actions — folds the secondary toolbar controls (Recompute Pricing,
                Export, Build Handoff, Citations) into one overflow menu so the bar stays
                uncluttered, especially on mobile. */}
            <div className="relative" ref={actionsMenuRef}>
              <button
                onClick={() => setShowActionsMenu((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={showActionsMenu}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  showActionsMenu ? 'bg-bw-border/60 text-bw-ink' : 'bg-bw-surface-subtle text-bw-ink hover:bg-bw-border/60'
                }`}
                title="More actions"
              >
                <MoreVertical className="h-3.5 w-3.5" />
                <span className="hidden lg:inline">More</span>
              </button>
              {showActionsMenu && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-bw-surface border border-bw-border rounded-lg shadow-lg z-30 py-1">
                  {/* Recompute Pricing (deterministic, no credits) */}
                  <button
                    onClick={() => { setShowActionsMenu(false); void handleRecomputePricing(); }}
                    disabled={recomputingPricing || !proposal?.content_json}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-bw-ink hover:bg-bw-surface-subtle disabled:opacity-50 text-left"
                  >
                    <RefreshCw className={`h-4 w-4 text-bw-primary flex-shrink-0 ${recomputingPricing ? 'animate-spin' : ''}`} />
                    <span className="flex-1">{recomputingPricing ? 'Recomputing…' : 'Recompute Pricing'}</span>
                    <span className="text-[10px] text-bw-muted">free</span>
                  </button>
                  {/* Export */}
                  <button
                    onClick={() => { setShowActionsMenu(false); setShowExportDialog(true); }}
                    disabled={exporting || !proposal?.content_json}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-bw-ink hover:bg-bw-surface-subtle disabled:opacity-50 text-left"
                  >
                    <Download className="h-4 w-4 text-bw-primary flex-shrink-0" />
                    <span className="flex-1">Export (PDF / Word)</span>
                  </button>
                  {/* Build Handoff */}
                  <button
                    onClick={() => { setShowActionsMenu(false); setShowHandoffDialog(true); }}
                    disabled={!proposal?.content_json}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-bw-ink hover:bg-bw-surface-subtle disabled:opacity-50 text-left"
                  >
                    <Layers className="h-4 w-4 text-bw-primary flex-shrink-0" />
                    <span className="flex-1">Build Handoff</span>
                  </button>
                  {/* Citations (source-grounded only) */}
                  {isSourceGrounded && activeSection === 'content' && (
                    <>
                      <div className="my-1 border-t border-bw-border" />
                      <button
                        onClick={() => { setShowActionsMenu(false); setShowCitations(!showCitations); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-bw-ink hover:bg-bw-surface-subtle text-left"
                      >
                        <Layers className="h-4 w-4 text-bw-primary flex-shrink-0" />
                        <span className="flex-1">{showCitations ? 'Hide citations' : 'Show citations'}</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* AI Tools Toggle — primary control, always visible */}
            <button
              onClick={() => setRightPanelOpen(!rightPanelOpen)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                rightPanelOpen ? 'bg-bw-primary text-white' : 'bg-bw-primary-tint text-bw-primary hover:brightness-95'
              }`}
            >
              <Sparkles className="h-3 w-3" />
              <span className="hidden lg:inline">AI Tools</span>
              {criticalGaps.length > 0 && (
                <span className="w-4 h-4 bg-urgent text-white text-[10px] rounded-full flex items-center justify-center">
                  {criticalGaps.length}
                </span>
              )}
            </button>
        </div>
      </div>

      {/* ===== MAIN AREA: Left rail + Content + Right Panel ===== */}
      <div className="flex-1 flex overflow-hidden">
        {/* Persistent left section rail (desktop). Mobile uses the top-bar
            dropdown / hamburger drawer instead. */}
        <div className="hidden md:flex flex-col w-[212px] flex-shrink-0 bg-bw-surface border-r border-bw-border overflow-y-auto">
          {/* Views */}
          <div className="p-2.5">
            {leftNavSections.map((section) => {
              const on = activeSection === section.id;
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] mb-0.5 transition-colors ${
                    on ? 'bg-bw-primary text-white font-semibold' : 'text-bw-ink hover:bg-bw-surface-subtle'
                  }`}
                >
                  <Icon className="h-[15px] w-[15px] flex-shrink-0" />
                  {section.label}
                </button>
              );
            })}
          </div>

          {/* Sections — completion dots + progress */}
          {contentTabs.length > 0 && (
            <div className="px-2.5 pb-2.5 border-t border-bw-border pt-2">
              <p className="px-2.5 py-1.5 text-[10px] font-bold text-bw-muted uppercase tracking-wider font-mono">Sections</p>
              {contentTabs.map((tab) => {
                const Icon = tab.icon;
                const filled = !!String(getContent(tab.id) || '').trim();
                const isActive = activeSection === 'content' && activeTab === tab.id;
                const isAi = tab.id === 'feedback';
                // active = blue, has content = green, empty = hollow.
                const dotCls = isActive
                  ? 'bg-bw-primary border-bw-primary'
                  : filled
                    ? 'bg-success border-success'
                    : 'bg-transparent border-bw-border';
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveSection('content'); setActiveTab(tab.id); }}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12px] transition-colors ${
                      isActive
                        ? 'bg-bw-primary-tint text-bw-primary font-semibold'
                        : filled ? 'text-bw-ink hover:bg-bw-surface-subtle' : 'text-bw-muted hover:bg-bw-surface-subtle'
                    }`}
                  >
                    {isAi
                      ? <Sparkles className="h-3 w-3 text-bw-primary flex-shrink-0" />
                      : <span className={`w-2 h-2 rounded-full border-[1.5px] flex-shrink-0 ${dotCls}`} />}
                    <Icon className="h-[14px] w-[14px] flex-shrink-0" />
                    <span className="truncate">{tab.label}</span>
                  </button>
                );
              })}
              {(() => {
                const done = contentTabs.filter(t => String(getContent(t.id) || '').trim()).length;
                const pct = contentTabs.length ? Math.round((done / contentTabs.length) * 100) : 0;
                return (
                  <div className="mx-1 mt-2.5 p-2.5 bg-bw-surface-subtle rounded-lg">
                    <div className="flex items-center justify-between text-[11px] font-semibold text-bw-ink mb-1.5">
                      <span>Progress</span>
                      <span className="font-mono">{done} / {contentTabs.length} sections</span>
                    </div>
                    <div className="w-full h-1 bg-fit-track rounded-full overflow-hidden">
                      <div className="h-full bg-bw-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Content Area — min-w-0 lets this flex child shrink so wide tables/grids
            scroll WITHIN it instead of pushing the whole page past the viewport. */}
        <div className="flex-1 min-w-0 overflow-auto p-4">
          {/* Proposal Content View */}
          {activeSection === 'content' && (
            <div className="bg-bw-surface rounded-xl shadow-sm border border-bw-border h-full flex flex-col">
              {/* Live Demonstration link — a deployed demo (e.g. from the Build Handoff)
                  to include with the submission. Stored on content_json.demoLink and
                  carried into exports. Always visible so it can be added/edited anytime. */}
              <div className="px-4 py-2.5 border-b border-bw-border bg-bw-primary-tint/40 flex items-center gap-2 flex-shrink-0">
                <ExternalLink className="h-4 w-4 text-bw-primary flex-shrink-0" />
                {editingDemoLink ? (
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <input
                      type="url"
                      autoFocus
                      value={demoLinkDraft}
                      onChange={(e) => setDemoLinkDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void saveDemoLink(demoLinkDraft);
                        if (e.key === 'Escape') setEditingDemoLink(false);
                      }}
                      placeholder="https://your-demo.netlify.app"
                      className="flex-1 min-w-0 text-sm px-2 py-1 rounded border border-bw-border bg-bw-surface text-bw-ink"
                    />
                    <button
                      onClick={() => void saveDemoLink(demoLinkDraft)}
                      className="flex items-center gap-1 px-2 py-1 bg-success text-white rounded text-xs font-medium hover:brightness-110"
                    >
                      <Save className="h-3 w-3" /> Save
                    </button>
                    <button
                      onClick={() => setEditingDemoLink(false)}
                      className="px-2 py-1 text-xs text-bw-muted hover:text-bw-ink"
                    >
                      Cancel
                    </button>
                  </div>
                ) : currentDemoLink ? (
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xs font-semibold text-bw-primary flex-shrink-0">Live Demonstration:</span>
                    <a
                      href={currentDemoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-bw-primary underline truncate hover:brightness-110"
                      title={currentDemoLink}
                    >
                      {currentDemoLink}
                    </a>
                    <button
                      onClick={() => { setDemoLinkDraft(currentDemoLink); setEditingDemoLink(true); }}
                      className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-bw-primary hover:bg-bw-primary-tint rounded flex-shrink-0"
                    >
                      <Edit2 className="h-3 w-3" /> Edit
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setDemoLinkDraft(''); setEditingDemoLink(true); }}
                    className="text-sm text-bw-primary font-medium hover:underline"
                  >
                    + Add a live demonstration link to this submission
                  </button>
                )}
              </div>

              {/* Section Header */}
              {(isOrchestrated || isSourceGrounded) && activeTab !== 'feedback' && activeTab !== 'contractAnalysis' && activeTab !== 'profileAnalysis' && activeTab !== 'deliverables' && activeTab !== 'product' && !isEditMode && (
                <div className="flex items-center justify-between p-4 border-b border-bw-border flex-shrink-0">
                  <h3 className="font-semibold text-bw-ink">{getCurrentSectionTitle()}</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowProfileSourceModal(true)}
                      className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-bw-primary bg-bw-primary-tint hover:brightness-95 rounded transition-colors"
                    >
                      <Info className="h-3 w-3" />
                      <span className="hidden sm:inline">Source</span>
                    </button>
                    <button
                      onClick={() => handleOpenFeedbackDialog(activeTab, getCurrentSectionTitle())}
                      disabled={regenerating}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-bw-primary bg-bw-primary-tint hover:brightness-95 rounded transition-colors disabled:opacity-50"
                    >
                      {regenerating && regeneratingSectionType === activeTab ? (
                        <RefreshCw className="h-3 w-3 animate-spin" />
                      ) : (
                        <Sparkles className="h-3 w-3" />
                      )}
                      <span className="hidden sm:inline">Enhance</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Edit Mode Header */}
              {isEditMode && (
                <div className="flex items-center justify-between p-4 border-b border-fit-moderate-border bg-fit-moderate-bg flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <Edit2 className="h-4 w-4 text-fit-moderate" />
                    <span className="font-medium text-fit-moderate text-sm">
                      Editing: {getCurrentSectionTitle()}
                      {activeIsStructuredText && (
                        <span className="ml-2 font-normal text-fit-moderate/80">— click any paragraph to edit · autosaves</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setIsEditMode(false);
                        setEditedContent(proposal.content_json);
                        setHasUnsavedChanges(false);
                      }}
                      className="px-3 py-1 text-xs text-bw-muted hover:text-bw-ink"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center px-3 py-1 bg-success text-white rounded hover:brightness-110 disabled:opacity-50 text-xs"
                    >
                      {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                      Save
                    </button>
                  </div>
                </div>
              )}

              {/* Content Area */}
              <div className="flex-1 overflow-auto p-4 sm:p-6">
                {isEditMode && !activeIsStructuredText ? (
                  /* Plain-text / non-structured sections: raw editable text. */
                  <textarea
                    value={getContent(activeTab)}
                    onChange={(e) => {
                      const newContent = { ...editedContent };
                      if (['contractAnalysis', 'profileAnalysis', 'coverLetter', 'feedback'].includes(activeTab)) {
                        newContent[activeTab] = e.target.value;
                      } else if (newContent.technicalSections) {
                        const idx = newContent.technicalSections.findIndex(
                          (s: any) => s.title.toLowerCase().replace(/\s+/g, '_') === activeTab
                        );
                        if (idx !== -1) {
                          newContent.technicalSections[idx].content = e.target.value;
                        }
                      }
                      setEditedContent(newContent);
                      editedContentRef.current = newContent;
                      setHasUnsavedChanges(true);
                      scheduleAutosave();
                    }}
                    className="w-full h-full min-h-[400px] p-4 font-mono text-sm border border-bw-border rounded-lg resize-none focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                    placeholder="Enter content here..."
                  />
                ) : (
                  /* Structured sections (and all view mode): click-to-edit fields. */
                  <ProposalEditProvider value={{ editing: isEditMode, onFieldChange: handleFieldChange }}>
                    <div className="prose prose-slate max-w-none">
                      {renderContentTab()}
                    </div>
                  </ProposalEditProvider>
                )}
              </div>
            </div>
          )}

          {/* Gap Report View */}
          {activeSection === 'gaps' && (
            <div className="space-y-6">
              <RedTeamPanel
                verdict={redTeamVerdict}
                runningRedTeam={runningRedTeam}
                onRun={handleRunRedTeam}
                currentSnapshot={currentContentSnapshot}
              />
              <GapReportSection
                gapReport={gapReport}
                winAssessment={winAssessment}
                improvementRoadmap={improvementRoadmap}
                orchestratedFeedback={proposal.content_json?.feedback}
                onNavigateToProfile={handleNavigateToProfile}
              />
            </div>
          )}

          {/* Source Map View */}
          {activeSection === 'sources' && sourceMap && (
            <SourceMapSection sourceMap={sourceMap} />
          )}

          {/* Evidence Library View */}
          {activeSection === 'evidence' && sourceGroundedOutput && (
            <EvidenceLibrarySection evidence={sourceGroundedOutput} />
          )}

          {/* Win Checklist View */}
          {activeSection === 'roadmap' && (
            <div className="space-y-6">
              <RedTeamPanel
                verdict={redTeamVerdict}
                runningRedTeam={runningRedTeam}
                onRun={handleRunRedTeam}
                currentSnapshot={currentContentSnapshot}
              />
              <WinChecklistSection
                improvementRoadmap={improvementRoadmap}
                winAssessment={winAssessment}
                onNavigateToProfile={handleNavigateToProfile}
              />
            </div>
          )}
        </div>

        {/* ===== RIGHT PANEL - AI Tools (Shares space with content) ===== */}
      {rightPanelOpen && (
        <div className="w-72 lg:w-80 xl:w-96 max-w-[40%] bg-bw-surface border-l border-bw-border flex flex-col shrink-0">
          {/* Right Panel Header */}
          <div className="flex items-center justify-between p-3 border-b border-bw-border bg-bw-surface-subtle">
            <div className="flex items-center gap-1">
              {[
                { id: 'chat' as RightPanelTool, icon: MessageSquare, label: 'Chat' },
                { id: 'gaps' as RightPanelTool, icon: AlertTriangle, label: 'Gaps', badge: criticalGaps.length },
                { id: 'coverage' as RightPanelTool, icon: ListChecks, label: 'Coverage', badge: coverage?.summary?.uncovered },
                { id: 'analysis' as RightPanelTool, icon: BarChart3, label: 'Analysis' },
                { id: 'costing' as RightPanelTool, icon: DollarSign, label: 'Costing' },
              ].map(tool => (
                <button
                  key={tool.id}
                  onClick={() => setRightPanelTool(tool.id)}
                  className={`relative flex items-center gap-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                    rightPanelTool === tool.id
                      ? 'bg-bw-primary text-white'
                      : 'text-bw-muted hover:bg-gray-200'
                  }`}
                >
                  <tool.icon className="h-3 w-3" />
                  <span className="hidden lg:inline">{tool.label}</span>
                  {tool.badge && tool.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-urgent-bg0 text-white text-[10px] rounded-full flex items-center justify-center">
                      {tool.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => setRightPanelOpen(false)}
              className="p-1 hover:bg-gray-200 rounded"
            >
              <X className="h-4 w-4 text-bw-muted" />
            </button>
          </div>

          {/* Right Panel Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* AI Chat */}
            {rightPanelTool === 'chat' && (
              <>
                <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-3 space-y-3">
                  {chatMessages.length === 0 && (
                    <div className="text-center py-8">
                      <Sparkles className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                      <p className="text-bw-muted text-sm font-medium">AI Assistant</p>
                      <p className="text-xs text-bw-muted mt-1">
                        Ask about your proposal
                      </p>
                      <div className="flex flex-wrap justify-center gap-1 mt-4">
                        {['Improve this section', 'What am I missing?', 'Pricing help'].map((s) => (
                          <button
                            key={s}
                            onClick={() => setChatInput(s)}
                            className="px-2 py-1 bg-bw-surface-subtle hover:bg-gray-200 text-bw-muted text-xs rounded transition-colors"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                      <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                        msg.role === 'user'
                          ? 'bg-bw-primary text-white'
                          : 'bg-bw-surface-subtle text-bw-ink'
                      }`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          className={`prose prose-sm max-w-none ${
                            msg.role === 'user'
                              ? 'prose-invert prose-p:text-white prose-strong:text-white prose-li:text-white'
                              : 'prose-gray'
                          } prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:mt-2 prose-headings:mb-1`}
                          components={{
                            // Ensure code blocks render properly
                            code: ({ node, inline, className, children, ...props }: any) => (
                              inline ? (
                                <code className={`${msg.role === 'user' ? 'bg-white/20 text-white' : 'bg-gray-200 text-bw-ink'} px-1 py-0.5 rounded text-xs font-mono`} {...props}>
                                  {children}
                                </code>
                              ) : (
                                <pre className={`${msg.role === 'user' ? 'bg-white/10' : 'bg-gray-200'} p-2 rounded text-xs overflow-x-auto`}>
                                  <code {...props}>{children}</code>
                                </pre>
                              )
                            ),
                            // Ensure links are clickable and visible
                            a: ({ children, href, ...props }: any) => (
                              <a
                                href={href}
                                className={msg.role === 'user' ? 'text-blue-200 underline' : 'text-bw-primary underline'}
                                target="_blank"
                                rel="noopener noreferrer"
                                {...props}
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                      {/* Apply this suggestion to the section you're viewing — drafts a
                          change you review & approve before anything is saved. */}
                      {msg.role === 'assistant' && activeSectionEditable && (
                        <button
                          onClick={() => proposeSectionEdit({
                            sectionType: activeTab,
                            sectionTitle: getCurrentSectionTitle(),
                            instruction: msg.content,
                            source: 'chat',
                          })}
                          disabled={!!aiEdit}
                          className="mt-1 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-bw-primary bg-bw-primary-tint hover:brightness-95 rounded disabled:opacity-50"
                          title={`Draft a change to ${getCurrentSectionTitle()} from this suggestion (you approve before it's saved)`}
                        >
                          <Sparkles className="h-3 w-3" />
                          Apply to {getCurrentSectionTitle()}
                        </button>
                      )}
                    </div>
                  ))}

                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-bw-surface-subtle rounded-lg px-3 py-2">
                        <Loader2 className="h-4 w-4 animate-spin text-bw-muted" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-3 border-t border-bw-border">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder="Ask AI..."
                      className="flex-1 px-3 py-2 border border-bw-border rounded text-sm focus:ring-2 focus:ring-bw-primary focus:border-transparent"
                      disabled={chatLoading}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!chatInput.trim() || chatLoading}
                      className="px-3 py-2 bg-bw-primary hover:bg-bw-primary text-white rounded transition-colors disabled:opacity-50"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Gap Resolution */}
            {rightPanelTool === 'gaps' && (
              <div className="flex-1 overflow-y-auto p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-bw-ink text-sm">Gap Resolution</h3>
                  <button
                    onClick={refreshAnalysis}
                    disabled={analysisRefreshing}
                    className="p-1 hover:bg-bw-surface-subtle rounded"
                  >
                    <RefreshCw className={`h-4 w-4 text-bw-muted ${analysisRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {/* Fast, free fix for the most common critical gap: fill bracket
                    placeholders ([COMPANY NAME]/[UEI]/[DATE]/…) from your profile —
                    no AI call. Preview → approve. */}
                <button
                  onClick={handleFixPlaceholders}
                  className="w-full flex items-center justify-center gap-1.5 mb-3 px-3 py-2 bg-bw-primary-tint text-bw-primary rounded-lg text-xs font-semibold hover:brightness-95"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Fill placeholders from profile (free)
                </button>

                {analysisLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-bw-primary" />
                  </div>
                ) : (
                  <GapResolutionCenter
                    criticalGaps={criticalGaps}
                    improvementOpportunities={improvementOpportunities}
                    strengths={strengths}
                    onFixGap={handleFixGap}
                    onFixAll={handleFixAllGaps}
                    onNavigateToProfile={handleNavigateToProfile}
                    compact
                  />
                )}
              </div>
            )}

            {/* AI Analysis */}
            {rightPanelTool === 'analysis' && (
              <div className="flex-1 overflow-y-auto p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-bw-ink text-sm">AI Analysis</h3>
                  <button
                    onClick={refreshAnalysis}
                    disabled={analysisRefreshing}
                    className="text-xs text-bw-primary hover:text-bw-primary"
                  >
                    {analysisRefreshing ? 'Running...' : 'Run Analysis'}
                  </button>
                </div>

                {analysisLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-bw-primary" />
                  </div>
                ) : analysis ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                      <div className={`rounded-lg p-3 ${
                        analysis.overallScore >= 70 ? 'bg-success-bg' :
                        analysis.overallScore >= 50 ? 'bg-fit-moderate-bg' : 'bg-urgent-bg'
                      }`}>
                        <p className="text-xs text-bw-muted">Score</p>
                        <p className="text-2xl font-bold">{analysis.overallScore}</p>
                      </div>
                      <div className="bg-bw-primary-tint rounded-lg p-3">
                        <p className="text-xs text-bw-muted">Win Prob</p>
                        <p className="text-2xl font-bold text-bw-primary">{analysis.winProbability}%</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-medium text-bw-muted mb-2">Section Scores</p>
                      <div className="space-y-2">
                        {Object.entries(analysis.sectionAnalyses).slice(0, 6).map(([id, section]) => (
                          <div key={id} className="flex items-center justify-between">
                            <span className="text-xs text-bw-ink truncate flex-1">{section.sectionTitle}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    section.score >= 70 ? 'bg-success-bg0' :
                                    section.score >= 50 ? 'bg-fit-moderate-bg0' : 'bg-urgent-bg0'
                                  }`}
                                  style={{ width: `${section.score}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium w-6">{section.score}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BarChart3 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-bw-muted">Click "Run Analysis"</p>
                  </div>
                )}
              </div>
            )}

            {/* Requirement Coverage matrix */}
            {rightPanelTool === 'coverage' && (
              <RequirementCoveragePanel
                coverage={coverage}
                loading={coverageLoading}
                onRun={handleCheckCoverage}
                onAddress={handleAddressRequirement}
                onAcknowledge={(item) => acknowledgeRequirements([item.id])}
                onAcknowledgeAll={() => acknowledgeRequirements(null)}
                addressDisabled={!!aiEdit}
              />
            )}

            {/* Costing Studio */}
            {rightPanelTool === 'costing' && (
              <div className="flex-1 overflow-y-auto p-3">
                <h3 className="font-semibold text-bw-ink text-sm mb-3">Pricing for this Proposal</h3>
                <div className="space-y-2">
                  {/* Proposal-specific pricing tools */}
                  <button
                    onClick={() => navigate(`/workspace/${id}/pricing`)}
                    className="w-full flex items-center gap-3 p-3 bg-bw-primary text-white rounded-lg hover:opacity-90 transition-opacity"
                  >
                    <Sparkles className="h-5 w-5" />
                    <div className="text-left">
                      <p className="font-medium text-sm">AI Generate Pricing</p>
                      <p className="text-xs text-blue-100">Auto-generate CLINs, labor & rates</p>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate(`/workspace/${id}/price-to-win`)}
                    className="w-full flex items-center gap-3 p-3 bg-bw-surface-subtle rounded-lg hover:bg-bw-surface-subtle transition-colors"
                  >
                    <Target className="h-5 w-5 text-bw-muted" />
                    <div className="text-left">
                      <p className="font-medium text-sm text-bw-ink">Price-to-Win Analysis</p>
                      <p className="text-xs text-bw-muted">Competitive pricing strategy</p>
                    </div>
                  </button>

                  <div className="pt-3 border-t border-bw-border mt-3">
                    <p className="text-xs text-bw-muted mb-2">Global Pricing Setup</p>
                    <button
                      onClick={() => navigate('/workspace/costing/wizard')}
                      className="w-full flex items-center gap-3 p-2 bg-bw-surface-subtle rounded-lg hover:bg-bw-surface-subtle transition-colors"
                    >
                      <Sparkles className="h-4 w-4 text-bw-primary" />
                      <div className="text-left">
                        <p className="font-medium text-xs text-bw-ink">Pricing Wizard</p>
                        <p className="text-xs text-bw-muted">Configure labor rates & overhead</p>
                      </div>
                    </button>

                    <button
                      onClick={() => navigate('/workspace/costing')}
                      className="w-full flex items-center gap-3 p-2 mt-2 bg-bw-surface-subtle rounded-lg hover:bg-bw-surface-subtle transition-colors"
                    >
                      <DollarSign className="h-4 w-4 text-success" />
                      <div className="text-left">
                        <p className="font-medium text-xs text-bw-ink">Costing Studio</p>
                        <p className="text-xs text-bw-muted">Full pricing dashboard</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      {/* Dialogs */}
      {/* Approval gate for AI-proposed section edits (Chat "Apply" / Gap fixes). */}
      <AiEditReviewDialog
        edit={aiEdit}
        applying={applyingAiEdit}
        onApply={applyPendingEdit}
        onDiscard={discardPendingEdit}
      />

      {/* Deterministic placeholder fill — preview before saving. */}
      <PlaceholderReviewDialog
        scan={placeholderScan}
        applying={applyingPlaceholders}
        onApply={applyPlaceholders}
        onCancel={() => { if (!applyingPlaceholders) setPlaceholderScan(null); }}
      />

      <RecomputePricingReviewDialog
        isOpen={!!recomputeReview}
        rows={recomputeReview?.rows || []}
        applying={applyingRecompute}
        onAccept={handleAcceptRecompute}
        onReject={handleRejectRecompute}
      />

      {showFeedbackDialog && (
        <SectionFeedbackDialog
          isOpen={showFeedbackDialog}
          onClose={() => setShowFeedbackDialog(false)}
          onSubmit={handleRegenerateSection}
          sectionType={regeneratingSectionType || ''}
          sectionTitle={regeneratingSectionTitle}
          isSubmitting={regenerating}
        />
      )}

      {showProfileSourceModal && (
        <ProfileSourceModal
          isOpen={showProfileSourceModal}
          onClose={() => setShowProfileSourceModal(false)}
          sectionType={activeTab}
          sectionTitle={getCurrentSectionTitle()}
          onNavigateToProfile={handleNavigateToProfile}
        />
      )}

      {/* Deliverable Feedback Dialog - for both section and individual enhancement */}
      {showDeliverableFeedbackDialog && (
        <DeliverableFeedbackDialog
          isOpen={showDeliverableFeedbackDialog}
          onClose={() => {
            setShowDeliverableFeedbackDialog(false);
            setSelectedDeliverableForEnhancement(null);
          }}
          onSubmit={(feedback) => {
            if (deliverableFeedbackMode === 'individual' && selectedDeliverableForEnhancement) {
              handleEnhanceIndividualDeliverable({
                ...feedback,
                deliverableId: selectedDeliverableForEnhancement.id,
                deliverableName: selectedDeliverableForEnhancement.name,
              });
            } else {
              handleEnhanceDeliverablesSection(feedback);
            }
          }}
          mode={deliverableFeedbackMode}
          deliverableId={selectedDeliverableForEnhancement?.id}
          deliverableName={selectedDeliverableForEnhancement?.name}
          isSubmitting={deliverableFeedbackMode === 'individual' ? enhancingDeliverable : enhancingDeliverableSection}
        />
      )}

      {/* Product Feedback Dialog */}
      {showProductFeedbackDialog && (
        <ProductFeedbackDialog
          isOpen={showProductFeedbackDialog}
          onClose={() => setShowProductFeedbackDialog(false)}
          onSubmit={handleEnhanceProductSection}
          productName={editedContent?.deliverablesData?.product_definition?.name}
          isSubmitting={enhancingProductSection}
        />
      )}

      {showExportDialog && proposal?.content_json && proposal?.contract && (
        <ExportDialog
          proposal={proposal.content_json}
          contract={proposal.contract}
          onClose={() => setShowExportDialog(false)}
          showCitations={showCitations}
          profile={profile}
        />
      )}

      {showHandoffDialog && proposal?.content_json && proposal?.contract && (
        <BuildHandoffDialog
          proposal={proposal.content_json}
          contract={proposal.contract}
          onClose={() => setShowHandoffDialog(false)}
        />
      )}
    </div>
  );

  // Helper function to render content tab
  function renderContentTab() {
    // Contract Analysis Tab - Shows contract summary and extracted requirements
    if (activeTab === 'contractAnalysis') {
      return (
        <ContractAnalysisTab
          contract={proposal.contract}
          contentJson={proposal.content_json}
          sourceGroundedOutput={sourceGroundedOutput}
          gapReport={gapReport}
          contractRequirements={contractRequirements}
          loadingRequirements={loadingRequirements}
          redTeamVerdict={redTeamVerdict}
          onRunRedTeam={handleRunRedTeam}
          runningRedTeam={runningRedTeam}
        />
      );
    }

    if (activeTab === 'product' && editedContent?.deliverablesData) {
      return (
        <ProductSynthesis
          productDefinition={editedContent.deliverablesData.product_definition}
          onRegenerate={handleSynthesizeProduct}
          onEnhance={() => setShowProductFeedbackDialog(true)}
          isRegenerating={regeneratingDeliverables}
          isEnhancing={enhancingProductSection}
        />
      );
    }

    if (activeTab === 'product' && !editedContent?.deliverablesData) {
      return (
        <div className="text-center py-12 bg-bw-surface-subtle rounded-lg border border-bw-border">
          <Sparkles className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-bw-ink">Product Synthesis Pending</p>
          <p className="text-sm text-bw-muted mt-1 mb-4">Generate deliverables first.</p>
          <button
            onClick={handleRegenerateDeliverables}
            disabled={regeneratingDeliverables}
            className="inline-flex items-center gap-2 px-4 py-2 bg-bw-primary hover:brightness-110 text-white font-medium rounded-lg transition-colors disabled:opacity-50 text-sm"
          >
            {regeneratingDeliverables ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
            Generate
          </button>
        </div>
      );
    }

    if (activeTab === 'deliverables' && editedContent?.deliverablesData) {
      return (
        <DeliverablesSectionContent
          deliverables={editedContent.deliverablesData}
          onEnhanceSection={(id) => {
            // Find the deliverable to get its name
            const deliverable = editedContent.deliverablesData.deliverables_with_labor?.find(
              (d: any) => d.deliverable_id === id
            );
            const name = deliverable?.deliverable_name || 'Deliverable';
            handleOpenDeliverableFeedback('individual', id, name);
          }}
          onEnhanceDeliverables={() => handleOpenDeliverableFeedback('section')}
          onRegenerateDeliverables={handleRegenerateDeliverables}
          onSaveDeliverables={handleSaveDeliverables}
          isSaving={savingDeliverables}
          isEnhancing={enhancingDeliverable}
          enhancingId={enhancingDeliverableId || undefined}
          isRegenerating={regeneratingDeliverables}
          isEnhancingSection={enhancingDeliverableSection}
        />
      );
    }

    if (activeTab === 'deliverables' && !editedContent?.deliverablesData) {
      return (
        <div className="text-center py-12 bg-bw-surface-subtle rounded-lg border border-bw-border">
          <FileText className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-bw-ink">Deliverables Analysis Pending</p>
          <p className="text-sm text-bw-muted mt-1 mb-4">Analyze contract requirements.</p>
          <button
            onClick={handleRegenerateDeliverables}
            disabled={regeneratingDeliverables}
            className="inline-flex items-center gap-2 px-4 py-2 bg-bw-primary hover:brightness-110 text-white font-medium rounded-lg transition-colors disabled:opacity-50 text-sm"
          >
            {regeneratingDeliverables ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
            Generate Deliverables
          </button>
        </div>
      );
    }

    if (activeTab === 'feedback' && proposal.content_json?.aiFeedback?.overallAssessment) {
      return (
        <AIFeedbackPanel
          feedback={proposal.content_json.aiFeedback}
          onNavigateToProfile={handleNavigateToProfile}
        />
      );
    }

    if (activeTab === 'feedback' && proposal.content_json?.aiFeedback) {
      return <SimpleFeedbackDisplay feedback={proposal.content_json.aiFeedback} />;
    }

    if (activeTab === 'feedback' && isSourceGrounded && winAssessment) {
      return (
        <SourceGroundedFeedbackDisplay
          winAssessment={winAssessment}
          gapReport={gapReport}
          onNavigateToProfile={handleNavigateToProfile}
        />
      );
    }

    if (activeTab === 'feedback') {
      return (
        <div className="text-center py-12 bg-bw-surface-subtle rounded-lg border border-bw-border">
          <Zap className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-bw-ink">AI Feedback Not Available</p>
          <p className="text-sm text-bw-muted mt-1">
            {isSourceGrounded ? 'Check Gap Report and Win Checklist.' : 'Regenerate proposal for feedback.'}
          </p>
        </div>
      );
    }

    if (isSourceGrounded && showCitations) {
      return (
        <CitationHighlight
          content={activeTab === 'coverLetter'
            ? formatCoverLetter(getContent(activeTab) || 'No content available.')
            : smartNormalize(getContent(activeTab) || 'No content available.')}
          citations={allCitations}
          evidence={new Map()}
          onNavigateToProfile={handleNavigateToProfile}
          showCitations={showCitations}
        />
      );
    }

    return (
      <JobDescriptionProvider contract={proposal?.contract} profile={profile} proposalContent={proposal?.content_json}>
        <ProposalSectionRenderer
          sectionType={activeTab}
          content={getContent(activeTab) || 'No content available.'}
          showCitations={showCitations}
        />
      </JobDescriptionProvider>
    );
  }
}

// ===== Helper Components =====

// Contract Analysis Tab - Shows contract summary and extracted requirements
function ContractAnalysisTab({
  contract,
  contentJson,
  sourceGroundedOutput,
  gapReport,
  contractRequirements = [],
  loadingRequirements = false,
  redTeamVerdict,
  onRunRedTeam,
  runningRedTeam = false,
}: {
  contract: any;
  contentJson: any;
  sourceGroundedOutput?: SourceGroundedProposalOutput;
  gapReport?: GapReport;
  contractRequirements?: any[];
  loadingRequirements?: boolean;
  redTeamVerdict?: SubmitabilityVerdict;
  onRunRedTeam?: () => void;
  runningRedTeam?: boolean;
}) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    description: false,
    requirements: true,
    evaluation: false,
    attachments: false,
    analysis: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Extract requirements from multiple sources.
  // PRIMARY: the Red Team compliance matrix — it's the only source graded against the
  // FULL solicitation truth AND the FINISHED proposal (requirement → met/at-risk/missing
  // + the section that covers it), so "checked off vs outstanding" reflects this proposal,
  // not a stale profile-match snapshot from contract-matching time.
  const extractedRequirements = useMemo(() => {
    const matrix = redTeamVerdict?.complianceMatrix;
    if (matrix && matrix.length > 0) {
      return matrix.map((m, i) => ({
        id: `RT-${i + 1}`,
        text: m.requirement,
        category: 'compliance',
        priority: m.status === 'missing' ? 'critical' : m.status === 'at-risk' ? 'medium' : 'low',
        mandatory: m.status !== 'met',
        matchStatus: m.status === 'met' ? 'FULL' : m.status === 'at-risk' ? 'PARTIAL' : 'NOT_MET',
        source: m.source || 'Solicitation',
        proposalSection: m.proposalSection,
        note: m.note,
      }));
    }

    // 1. Database contract_requirements (SAM match-time snapshot — profile match, not proposal coverage).
    if (contractRequirements && contractRequirements.length > 0) {
      return contractRequirements.map((req, i) => ({
        id: req.id || `REQ-${i + 1}`,
        text: req.requirement_text,
        category: req.requirement_type || req.category || 'technical',
        priority: req.priority || 'medium',
        mandatory: req.is_mandatory,
        matchStatus: req.match_score >= 80 ? 'FULL' : req.match_score >= 40 ? 'PARTIAL' : 'NOT_MET',
        source: req.source_section || 'Solicitation',
        evaluationWeight: req.evaluation_weight,
        keywords: req.keywords || [],
        matchedCapabilities: req.matched_capabilities || [],
        matchScore: req.match_score,
        gapAnalysis: req.gap_analysis,
      }));
    }

    // 2. Try sourceGroundedOutput extractedRequirements (from generation process)
    if (sourceGroundedOutput?.extractedRequirements && Array.isArray(sourceGroundedOutput.extractedRequirements)) {
      return sourceGroundedOutput.extractedRequirements;
    }

    // 2b. Also check for requirements property (legacy)
    if (sourceGroundedOutput?.requirements && Array.isArray(sourceGroundedOutput.requirements)) {
      return sourceGroundedOutput.requirements;
    }

    // 3. Build from gapReport (which has the matched requirements)
    if (gapReport) {
      const requirements: any[] = [];

      // Critical gaps = unmet requirements
      (gapReport.criticalGaps || []).forEach((gap, i) => {
        requirements.push({
          id: gap.requirementId || `CRIT-${i + 1}`,
          text: gap.requirementText,
          category: gap.category || 'technical',
          priority: 'critical',
          mandatory: true,
          matchStatus: 'NOT_MET',
          source: gap.requirementSource || 'Solicitation',
        });
      });

      // Improvement opportunities = partially met
      (gapReport.improvementOpportunities || []).forEach((opp, i) => {
        requirements.push({
          id: opp.requirementId || `IMP-${i + 1}`,
          text: opp.requirementText || opp.description,
          category: opp.category || 'technical',
          priority: opp.priority || 'medium',
          mandatory: false,
          matchStatus: 'PARTIAL',
          source: 'Solicitation',
        });
      });

      // Strengths = fully met
      (gapReport.strengthsToHighlight || []).forEach((str, i) => {
        requirements.push({
          id: `STR-${i + 1}`,
          text: str.description,
          category: str.category || 'technical',
          priority: 'low',
          mandatory: false,
          matchStatus: 'FULL',
          source: 'Solicitation',
        });
      });

      if (requirements.length > 0) return requirements;
    }

    // 4. Fallback to contentJson
    return contentJson?.extractedRequirements ||
      contentJson?.contractAnalysis?.requirements || [];
  }, [redTeamVerdict, contractRequirements, sourceGroundedOutput, gapReport, contentJson]);

  const evaluationCriteria = sourceGroundedOutput?.evaluationCriteria ||
    contentJson?.evaluationCriteria ||
    contentJson?.contractAnalysis?.evaluationCriteria || [];

  const attachments = contract?.attachments || contract?.resources || [];

  // Group requirements by status
  const requirementsByStatus = useMemo(() => {
    const critical = extractedRequirements.filter((r: any) => r.matchStatus === 'NOT_MET' || r.priority === 'critical');
    const partial = extractedRequirements.filter((r: any) => r.matchStatus === 'PARTIAL');
    const met = extractedRequirements.filter((r: any) => r.matchStatus === 'FULL');
    const other = extractedRequirements.filter((r: any) =>
      !['NOT_MET', 'PARTIAL', 'FULL'].includes(r.matchStatus) && r.priority !== 'critical'
    );
    return { critical, partial, met, other };
  }, [extractedRequirements]);

  // Collapsible section component
  const CollapsibleSection = ({
    id,
    title,
    icon: Icon,
    iconColor,
    badge,
    defaultExpanded = false,
    children
  }: {
    id: string;
    title: string;
    icon: any;
    iconColor: string;
    badge?: React.ReactNode;
    defaultExpanded?: boolean;
    children: React.ReactNode;
  }) => (
    <div className="bg-bw-surface rounded-xl shadow-sm border border-bw-border overflow-hidden">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 hover:bg-bw-surface-subtle transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className={`h-5 w-5 ${iconColor}`} />
          <h3 className="font-semibold text-bw-ink">{title}</h3>
          {badge}
        </div>
        <ChevronRight className={`h-5 w-5 text-bw-muted transition-transform ${expandedSections[id] ? 'rotate-90' : ''}`} />
      </button>
      {expandedSections[id] && (
        <div className="px-4 pb-4 border-t border-bw-border">{children}</div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Contract Overview Header - Always visible */}
      <div className="bg-bw-sidebar rounded-xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold mb-2">{contract?.title || 'Contract Details'}</h2>
            <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
              <span className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {contract?.agency || 'Unknown Agency'}
              </span>
              {contract?.naics_code && (
                <span className="flex items-center gap-1">
                  <Target className="h-4 w-4" />
                  NAICS: {contract.naics_code}
                </span>
              )}
              {contract?.set_aside && (
                <span className="px-2 py-0.5 bg-white/20 rounded text-xs">{contract.set_aside}</span>
              )}
            </div>
          </div>
          {contract?.funding_amount && (
            <div className="text-right">
              <p className="text-xs text-white/60">Est. Value</p>
              <p className="text-lg font-bold">${(contract.funding_amount / 1000000).toFixed(1)}M</p>
            </div>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-white/60">Response Due</p>
            <p className="text-sm font-medium">
              {contract?.response_deadline ? new Date(contract.response_deadline).toLocaleDateString() : 'TBD'}
            </p>
          </div>
          <div>
            <p className="text-xs text-white/60">Posted</p>
            <p className="text-sm font-medium">
              {contract?.posted_date ? new Date(contract.posted_date).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-white/60">Type</p>
            <p className="text-sm font-medium">{contract?.type || 'RFP'}</p>
          </div>
          <div>
            <p className="text-xs text-white/60">Notice ID</p>
            <p className="text-sm font-medium truncate">{contract?.notice_id || contract?.noticeId || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Description - Collapsible */}
      {contract?.description && (
        <CollapsibleSection id="description" title="Description" icon={FileText} iconColor="text-bw-primary">
          <p className="text-bw-ink text-sm leading-relaxed whitespace-pre-wrap mt-3">{contract.description}</p>
        </CollapsibleSection>
      )}

      {/* Requirements - Key section, expanded by default */}
      <CollapsibleSection
        id="requirements"
        title="Contract Requirements"
        icon={CheckCircle}
        iconColor="text-success"
        badge={
          extractedRequirements.length > 0 ? (
            <span className="ml-2 px-2 py-0.5 bg-bw-primary-tint text-bw-primary text-xs rounded-full">
              {extractedRequirements.length} identified
            </span>
          ) : null
        }
      >
        {/* Source + re-analyze control. The Red Team review is the only pass that grades
            requirements against the FINISHED proposal, so we surface it as the authority
            and let the user re-run it on demand (async — no 504). */}
        <div className="flex items-center justify-between gap-3 mt-3 mb-1 p-3 bg-bw-surface-subtle border border-bw-border rounded-lg">
          <div className="text-xs text-bw-muted">
            {redTeamVerdict ? (
              <>
                <span className="font-medium text-bw-ink">Coverage graded by Red Team review</span>
                {redTeamVerdict.assessedAt && (
                  <> · {new Date(redTeamVerdict.assessedAt).toLocaleString()}</>
                )}
                <span className="block mt-0.5">Status reflects the current proposal vs. the solicitation — re-run after edits to refresh.</span>
              </>
            ) : (
              <>
                <span className="font-medium text-bw-ink">Not yet graded against this proposal.</span>
                <span className="block mt-0.5">The list below is a match-time snapshot. Run the Red Team review to grade what's actually addressed vs. outstanding.</span>
              </>
            )}
          </div>
          {onRunRedTeam && (
            <button
              onClick={onRunRedTeam}
              disabled={runningRedTeam}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-bw-primary hover:brightness-110 text-white rounded-md text-xs font-medium transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${runningRedTeam ? 'animate-spin' : ''}`} />
              {runningRedTeam ? 'Analyzing…' : redTeamVerdict ? 'Re-analyze' : 'Analyze requirements'}
            </button>
          )}
        </div>
        {extractedRequirements.length > 0 ? (
          <div className="space-y-4 mt-3">
            {/* Critical/Unmet */}
            {requirementsByStatus.critical.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-urgent uppercase tracking-wide mb-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Critical / Unmet ({requirementsByStatus.critical.length})
                </h4>
                <div className="space-y-2">
                  {requirementsByStatus.critical.map((req: any, i: number) => (
                    <div key={i} className="p-3 bg-urgent-bg border border-urgent-border rounded-lg">
                      <p className="text-sm text-bw-ink">{req.text || req.requirementText}</p>
                      {req.note && <p className="text-xs text-bw-muted mt-1 italic">{req.note}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        {req.source && <span className="text-xs text-bw-muted">{req.source}</span>}
                        {req.category && <span className="text-xs text-urgent bg-urgent-bg px-1.5 py-0.5 rounded">{req.category}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Partial */}
            {requirementsByStatus.partial.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-fit-moderate uppercase tracking-wide mb-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Partially Met ({requirementsByStatus.partial.length})
                </h4>
                <div className="space-y-2">
                  {requirementsByStatus.partial.map((req: any, i: number) => (
                    <div key={i} className="p-3 bg-fit-moderate-bg border border-fit-moderate-border rounded-lg">
                      <p className="text-sm text-bw-ink">{req.text || req.requirementText}</p>
                      {req.note && <p className="text-xs text-bw-muted mt-1 italic">{req.note}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        {req.proposalSection && <span className="text-xs text-bw-muted">Covered in: {req.proposalSection}</span>}
                        {req.category && <span className="text-xs text-fit-moderate bg-fit-moderate-bg px-1.5 py-0.5 rounded">{req.category}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fully Met */}
            {requirementsByStatus.met.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-success uppercase tracking-wide mb-2 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Fully Met ({requirementsByStatus.met.length})
                </h4>
                <div className="space-y-2">
                  {requirementsByStatus.met.map((req: any, i: number) => (
                    <div key={i} className="p-3 bg-success-bg border border-success-border rounded-lg">
                      <p className="text-sm text-bw-ink">{req.text || req.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {req.proposalSection && <span className="text-xs text-bw-muted">Covered in: {req.proposalSection}</span>}
                        {req.category && <span className="text-xs text-success bg-success-bg px-1.5 py-0.5 rounded">{req.category}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Other */}
            {requirementsByStatus.other.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-bw-muted uppercase tracking-wide mb-2">Other ({requirementsByStatus.other.length})</h4>
                <div className="space-y-2">
                  {requirementsByStatus.other.map((req: any, i: number) => (
                    <div key={i} className="p-3 bg-bw-surface-subtle border border-bw-border rounded-lg">
                      <p className="text-sm text-bw-ink">{typeof req === 'string' ? req : req.text || req.requirement || req.description}</p>
                      {req.category && <span className="text-xs text-bw-muted bg-bw-surface-subtle px-1.5 py-0.5 rounded mt-1 inline-block">{req.category}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : loadingRequirements ? (
          <div className="text-center py-8 bg-bw-surface-subtle rounded-lg mt-3">
            <Loader2 className="h-8 w-8 text-bw-primary mx-auto mb-2 animate-spin" />
            <p className="text-bw-muted text-sm">Loading requirements...</p>
          </div>
        ) : (
          <div className="text-center py-8 bg-bw-surface-subtle rounded-lg mt-3">
            <AlertCircle className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-bw-muted text-sm">No requirements extracted yet.</p>
            <p className="text-bw-muted text-xs mt-1">Requirements are extracted during document analysis.</p>
            <p className="text-bw-muted text-xs">Upload and analyze contract documents to extract requirements.</p>
          </div>
        )}
      </CollapsibleSection>

      {/* Evaluation Criteria */}
      {evaluationCriteria.length > 0 && (
        <CollapsibleSection id="evaluation" title="Evaluation Criteria" icon={BarChart3} iconColor="text-bw-primary">
          <div className="space-y-2 mt-3">
            {evaluationCriteria.map((criteria: any, i: number) => (
              <div key={i} className="flex items-center justify-between p-3 bg-bw-primary-tint rounded-lg">
                <span className="text-sm font-medium text-bw-ink">
                  {typeof criteria === 'string' ? criteria : criteria.name || criteria.factor}
                </span>
                {criteria.weight && (
                  <span className="px-2 py-0.5 bg-bw-primary-tint text-bw-primary text-xs rounded font-medium">{criteria.weight}%</span>
                )}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Attachments */}
      {attachments.length > 0 && (
        <CollapsibleSection id="attachments" title="Attachments & Resources" icon={Package} iconColor="text-bw-primary">
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            {attachments.map((att: any, i: number) => (
              <a
                key={i}
                href={att.url || att.link || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-bw-surface-subtle rounded-lg hover:bg-bw-surface-subtle transition-colors"
              >
                <FileText className="h-5 w-5 text-bw-muted shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-bw-ink truncate">{att.name || att.title || `Attachment ${i + 1}`}</p>
                  {att.size && <p className="text-xs text-bw-muted">{att.size}</p>}
                </div>
                <ExternalLink className="h-4 w-4 text-bw-muted shrink-0" />
              </a>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* AI Analysis Summary */}
      {contentJson?.contractAnalysis && typeof contentJson.contractAnalysis === 'string' && (
        <CollapsibleSection id="analysis" title="AI Analysis Summary" icon={Sparkles} iconColor="text-fit-moderate">
          <div className="prose prose-sm max-w-none text-bw-ink mt-3">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentJson.contractAnalysis}</ReactMarkdown>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

function SimpleFeedbackDisplay({ feedback }: { feedback: any }) {
  const getScoreColor = (score: number) => {
    if (score >= 85) return 'text-success bg-success-bg';
    if (score >= 70) return 'text-bw-primary bg-bw-primary-tint';
    if (score >= 55) return 'text-fit-moderate bg-fit-moderate-bg';
    return 'text-urgent bg-urgent-bg';
  };

  const overallScore = feedback.overallScore || 0;
  const strengths = feedback.strengths || [];
  const recommendations = feedback.recommendations || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className={`rounded-lg p-3 ${getScoreColor(overallScore)}`}>
          <p className="text-xs font-medium opacity-80">Overall Score</p>
          <p className="text-2xl font-bold">{overallScore}</p>
        </div>
        <div className="bg-bw-primary-tint rounded-lg p-3">
          <p className="text-xs text-bw-primary font-medium">Win Probability</p>
          <p className="text-2xl font-bold text-bw-primary">{feedback.winProbability || 'N/A'}</p>
        </div>
        <div className={`rounded-lg p-3 ${getScoreColor(feedback.complianceScore || 0)}`}>
          <p className="text-xs font-medium opacity-80">Compliance</p>
          <p className="text-2xl font-bold">{feedback.complianceScore || 0}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-600 font-medium">Volumes</p>
          <p className="text-2xl font-bold text-slate-700">{feedback.volumesGenerated || 0}</p>
        </div>
      </div>

      {strengths.length > 0 && (
        <div className="bg-success-bg rounded-lg border border-green-200 p-4">
          <h3 className="font-semibold text-success mb-2 flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4" /> Strengths
          </h3>
          <ul className="space-y-1">
            {strengths.slice(0, 4).map((s: string, i: number) => (
              <li key={i} className="text-sm text-success flex items-start gap-2">
                <span className="text-success mt-0.5">✓</span>{s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="bg-fit-moderate-bg rounded-lg border border-amber-200 p-4">
          <h3 className="font-semibold text-fit-moderate mb-2 flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4" /> Recommendations
          </h3>
          <ul className="space-y-1">
            {recommendations.slice(0, 4).map((r: string, i: number) => (
              <li key={i} className="text-sm text-fit-moderate flex items-start gap-2">
                <span className="text-fit-moderate mt-0.5">→</span>{r}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// FNV-1a snapshot of the proposal content — must match the edge function so the
// workspace can detect "proposal changed since this Red Team review".
function rtSnapshot(text: string): string {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `${text.length}:${(h >>> 0).toString(16)}`;
}

// Build the same combined section string the reviewer hashes (titles + content).
function buildRedTeamContentString(contentJson: any): string {
  if (!contentJson) return '';
  const ts: Array<{ title: string; content: string }> = Array.isArray(contentJson.technicalSections) ? contentJson.technicalSections : [];
  const pick = (needle: string) => (ts.find((s) => (s.title || '').toLowerCase().includes(needle))?.content || '');
  const sections: Array<[string, string]> = [
    ['Cover Letter', contentJson.coverLetter || ''],
    ['Executive Summary', contentJson.executiveSummary || pick('executive')],
    ['Technical Approach', contentJson.technicalApproach || pick('technical')],
    ['Management Approach', contentJson.managementApproach || pick('management')],
    ['Past Performance', contentJson.pastPerformance || pick('past performance')],
    ['Key Personnel', contentJson.personnel || pick('personnel')],
    ['Pricing & Cost Strategy', contentJson.pricingStrategy || pick('pricing')],
    ['Compliance', contentJson.compliance || pick('compliance')],
  ];
  return sections.filter(([, c]) => c && String(c).trim()).map(([t, c]) => `## ${t}\n${c}`).join('\n\n');
}

const REC_STYLES: Record<string, { label: string; cls: string }> = {
  SUBMIT: { label: 'SUBMIT', cls: 'bg-success-bg text-success border-green-300' },
  SUBMIT_WITH_FIXES: { label: 'SUBMIT WITH FIXES', cls: 'bg-fit-moderate-bg text-fit-moderate border-fit-moderate-border' },
  DO_NOT_SUBMIT: { label: 'DO NOT SUBMIT', cls: 'bg-urgent-bg text-urgent border-urgent-border' },
};

// Adversarial Red Team verdict header: score badge, go/no-go, blockers, compliance
// matrix, delta vs prior run, "changed since review" notice, and the run/re-run button.
function RedTeamPanel({ verdict, runningRedTeam, onRun, currentSnapshot }: {
  verdict?: SubmitabilityVerdict;
  runningRedTeam: boolean;
  onRun: () => void;
  currentSnapshot: string;
}) {
  const rec = verdict ? (REC_STYLES[verdict.recommendation] || REC_STYLES.SUBMIT_WITH_FIXES) : null;
  const stale = !!verdict?.contentSnapshot && verdict.contentSnapshot !== currentSnapshot;
  const cmp = verdict?.comparisonToPrior;

  const RunButton = (
    <button
      onClick={onRun}
      disabled={runningRedTeam}
      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-bw-primary text-white hover:bg-indigo-700 disabled:opacity-60"
    >
      {runningRedTeam
        ? <><Loader2 className="h-4 w-4 animate-spin" /> Reviewing…</>
        : <><Shield className="h-4 w-4" /> {verdict ? 'Re-run Red Team review' : 'Run Red Team review'}</>}
    </button>
  );

  if (!verdict) {
    return (
      <div className="bg-bw-primary rounded-xl p-5 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h3 className="font-bold flex items-center gap-2"><Shield className="h-5 w-5" /> Red Team Submitability Review</h3>
          <p className="text-indigo-100 text-sm mt-1 max-w-2xl">
            A brutal adversarial pass over the full solicitation + your proposal — "would this get thrown out?"
            It flags fatal blockers, contradictions, placeholders, and pricing/personnel gaps. It only assesses; it never rewrites.
          </p>
        </div>
        <div className="shrink-0">{RunButton}</div>
      </div>
    );
  }

  const scoreColor = verdict.submitabilityScore >= 75 ? 'text-success' : verdict.submitabilityScore >= 50 ? 'text-fit-moderate' : 'text-urgent';
  const matrix = verdict.complianceMatrix || [];

  return (
    <div className="space-y-4">
      {/* Verdict header */}
      <div className="bg-bw-surface rounded-xl shadow-card border border-bw-border p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center min-w-[72px]">
              <span className={`text-4xl font-bold ${scoreColor}`}>{verdict.submitabilityScore}</span>
              <span className="text-[10px] uppercase tracking-wide text-bw-muted">/100 ready</span>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-bw-ink flex items-center gap-1.5"><Shield className="h-4 w-4 text-bw-primary" /> Red Team verdict</span>
                {rec && <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${rec.cls}`}>{rec.label}</span>}
                {verdict.meta?.deterministicOnly && (
                  <span className="text-[11px] text-fit-moderate">(checks only — AI pass didn't run)</span>
                )}
              </div>
              <p className="text-sm text-bw-ink mt-1 max-w-2xl">{verdict.summary}</p>
              <p className="text-xs text-bw-muted mt-1">
                Reviewed {new Date(verdict.assessedAt).toLocaleString()}
                {cmp && typeof cmp.scoreDelta === 'number' && (
                  <span className={cmp.scoreDelta >= 0 ? 'text-success' : 'text-urgent'}>
                    {' '}· since last run: {cmp.scoreDelta >= 0 ? '+' : ''}{cmp.scoreDelta} pts, {cmp.resolved.length} resolved, {cmp.emerged.length} new
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="shrink-0">{RunButton}</div>
        </div>

        {stale && (
          <div className="mt-3 flex items-start gap-2 text-xs bg-fit-moderate-bg border border-fit-moderate-border rounded-md p-2.5 text-fit-moderate">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>The proposal has changed since this review. Re-run the Red Team to refresh the verdict.</span>
          </div>
        )}
      </div>

      {/* Fatal blockers */}
      {verdict.fatalBlockers.length > 0 && (
        <div className="bg-urgent-bg border border-urgent-border rounded-xl p-4">
          <h4 className="font-semibold text-urgent flex items-center gap-1.5 mb-2 text-sm">
            <AlertCircle className="h-4 w-4" /> Fatal blockers ({verdict.fatalBlockers.length}) — must fix before submitting
          </h4>
          <ul className="space-y-2">
            {verdict.fatalBlockers.map((b) => (
              <li key={b.id} className="text-sm">
                <span className="font-medium text-bw-ink">{b.title}</span>
                {b.section && <span className="text-xs text-bw-muted"> · {b.section}</span>}
                {b.recommendedAction && <p className="text-xs text-bw-muted mt-0.5">→ {b.recommendedAction}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Human-required */}
      {verdict.humanRequired.length > 0 && (
        <div className="bg-bw-surface border border-bw-border rounded-xl p-4">
          <h4 className="font-semibold text-bw-ink flex items-center gap-1.5 mb-2 text-sm">
            <Users className="h-4 w-4 text-bw-primary" /> Needs you to confirm ({verdict.humanRequired.length})
          </h4>
          <ul className="space-y-1.5">
            {verdict.humanRequired.map((h) => (
              <li key={h.id} className="text-sm text-bw-ink flex gap-1.5"><ArrowRight className="h-3.5 w-3.5 text-bw-primary mt-0.5 shrink-0" />{h.title}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Compliance matrix */}
      {matrix.length > 0 && (
        <div className="bg-bw-surface border border-bw-border rounded-xl p-4">
          <h4 className="font-semibold text-bw-ink mb-3 text-sm">Compliance matrix ({matrix.length})</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-bw-muted border-b border-bw-border">
                  <th className="py-1.5 pr-3 font-medium">Requirement</th>
                  <th className="py-1.5 pr-3 font-medium">Status</th>
                  <th className="py-1.5 font-medium">Where</th>
                </tr>
              </thead>
              <tbody>
                {matrix.slice(0, 40).map((c, i) => {
                  const cls = c.status === 'met' ? 'bg-success-bg text-success'
                    : c.status === 'missing' ? 'bg-urgent-bg text-urgent' : 'bg-fit-moderate-bg text-fit-moderate';
                  return (
                    <tr key={i} className="border-b border-bw-border/50 align-top">
                      <td className="py-1.5 pr-3 text-bw-ink">{c.requirement}{c.source && <span className="text-xs text-bw-muted"> ({c.source})</span>}</td>
                      <td className="py-1.5 pr-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{c.status}</span></td>
                      <td className="py-1.5 text-xs text-bw-muted">{c.proposalSection || c.note || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function GapReportSection({
  gapReport,
  winAssessment,
  improvementRoadmap,
  orchestratedFeedback,
  onNavigateToProfile
}: {
  gapReport?: GapReport;
  winAssessment?: WinAssessment;
  improvementRoadmap?: ImprovementRoadmap;
  orchestratedFeedback?: any;
  onNavigateToProfile: (section: string) => void;
}) {
  const feedback = !gapReport && orchestratedFeedback?.type === 'orchestrated' ? {
    readinessScore: orchestratedFeedback.readinessScore || 0,
    gaps: orchestratedFeedback.gaps || { critical: [], recommended: [], optional: [] },
  } : null;

  if (!gapReport && !feedback) {
    return (
      <div className="bg-bw-surface rounded-xl shadow-sm border border-bw-border p-8 text-center">
        <AlertTriangle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-bw-muted">No gap analysis available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-3">
          <p className="text-xs text-bw-primary font-medium">Readiness</p>
          <p className="text-2xl font-bold text-bw-primary">{gapReport?.overallReadiness || feedback?.readinessScore || 0}%</p>
        </div>
        <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-3">
          <p className="text-xs text-success font-medium">Met</p>
          <p className="text-2xl font-bold text-success">{gapReport?.summary?.fullyMet || 0}</p>
        </div>
        <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-3">
          <p className="text-xs text-fit-moderate font-medium">Partial</p>
          <p className="text-2xl font-bold text-fit-moderate">{gapReport?.summary?.partiallyMet || 0}</p>
        </div>
        <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-3">
          <p className="text-xs text-urgent font-medium">Gaps</p>
          <p className="text-2xl font-bold text-urgent">{gapReport?.summary?.criticalGapsCount || 0}</p>
        </div>
      </div>

      {winAssessment && (
        <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-4">
          <h3 className="font-semibold text-bw-ink mb-3 flex items-center gap-2 text-sm">
            <TrendingUp className="h-4 w-4 text-bw-primary" /> Win Assessment
          </h3>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-fit-strong-stroke rounded-full"
                style={{ width: `${winAssessment.winProbabilityEstimate}%` }}
              />
            </div>
            <span className="text-xl font-bold text-bw-ink">{winAssessment.winProbabilityEstimate}%</span>
          </div>
          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
            winAssessment.competitivePosition === 'STRONG' ? 'bg-success-bg text-success' :
            winAssessment.competitivePosition === 'MODERATE' ? 'bg-fit-moderate-bg text-fit-moderate' :
            'bg-urgent-bg text-urgent'
          }`}>
            {winAssessment.competitivePosition?.replace(/_/g, ' ')}
          </span>
        </div>
      )}

      {(gapReport?.criticalGaps?.length || 0) > 0 && (
        <div className="bg-urgent-bg rounded-lg border border-red-200 p-4">
          <h3 className="font-semibold text-urgent mb-3 flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4" /> Critical Gaps ({gapReport?.criticalGaps?.length})
          </h3>
          <div className="space-y-2">
            {gapReport?.criticalGaps?.slice(0, 5).map((gap, i) => (
              <div key={i} className="bg-bw-surface rounded p-3 border border-red-100">
                <p className="font-medium text-red-900 text-sm">{gap.requirement}</p>
                <p className="text-xs text-urgent mt-1">{gap.recommendation}</p>
                {gap.profileAction && (
                  <button
                    onClick={() => onNavigateToProfile(gap.profileAction?.split('→')[1]?.trim().toLowerCase() || 'profile')}
                    className="flex items-center gap-1 mt-2 text-xs font-medium text-urgent hover:text-urgent"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {gap.profileAction}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SourceMapSection({ sourceMap }: { sourceMap: SourceMap }) {
  return (
    <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-4">
      <h3 className="font-semibold text-bw-ink mb-3 flex items-center gap-2 text-sm">
        <MapIcon className="h-4 w-4 text-bw-primary" /> Source Map
      </h3>
      <p className="text-sm text-bw-muted mb-3">Content traceability to your profile.</p>
      <div className="space-y-2">
        {sourceMap.sectionMappings?.map((m, i) => (
          <div key={i} className="p-3 bg-bw-surface-subtle rounded border border-bw-border">
            <p className="font-medium text-bw-ink text-sm">{m.sectionName}</p>
            <p className="text-xs text-bw-muted">{m.citations?.length || 0} citations</p>
          </div>
        )) || <p className="text-sm text-bw-muted text-center py-4">No mapping data.</p>}
      </div>
    </div>
  );
}

function EvidenceLibrarySection({ evidence }: { evidence: SourceGroundedProposalOutput }) {
  return (
    <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-4">
      <h3 className="font-semibold text-bw-ink mb-3 flex items-center gap-2 text-sm">
        <Database className="h-4 w-4 text-bw-primary" /> Evidence Library
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 bg-bw-primary-tint rounded text-center">
          <p className="text-xl font-bold text-bw-primary">{evidence.evidenceLibrary?.totalItems || 0}</p>
          <p className="text-xs text-bw-primary">Total</p>
        </div>
        <div className="p-3 bg-success-bg rounded text-center">
          <p className="text-xl font-bold text-success">{evidence.evidenceLibrary?.usedItems || 0}</p>
          <p className="text-xs text-success">Used</p>
        </div>
        <div className="p-3 bg-bw-primary-tint rounded text-center">
          <p className="text-xl font-bold text-bw-primary">{evidence.evidenceLibrary?.highRelevanceItems || 0}</p>
          <p className="text-xs text-bw-primary">High Relevance</p>
        </div>
        <div className="p-3 bg-fit-moderate-bg rounded text-center">
          <p className="text-xl font-bold text-fit-moderate">{evidence.evidenceLibrary?.inferredCapabilitiesCount || 0}</p>
          <p className="text-xs text-fit-moderate">Inferred</p>
        </div>
      </div>
    </div>
  );
}

function WinChecklistSection({
  improvementRoadmap,
  winAssessment,
  onNavigateToProfile
}: {
  improvementRoadmap?: ImprovementRoadmap;
  winAssessment?: WinAssessment;
  onNavigateToProfile: (section: string) => void;
}) {
  if (!improvementRoadmap && !winAssessment) {
    return (
      <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-8 text-center">
        <CheckCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-bw-muted">No improvement roadmap available.</p>
      </div>
    );
  }

  const actions = improvementRoadmap?.criticalForCompliance || improvementRoadmap?.immediateActions || [];
  const highImpact = improvementRoadmap?.highImpactOnWin || improvementRoadmap?.shortTermActions || [];
  const differentiators = improvementRoadmap?.differentiators || improvementRoadmap?.longTermActions || [];

  return (
    <div className="space-y-4">
      {actions.length > 0 && (
        <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-4">
          <h3 className="font-semibold text-fit-moderate mb-3 text-sm">Critical (Before Submission)</h3>
          <div className="space-y-2">
            {actions.map((a, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-orange-50 rounded border border-orange-200">
                <ArrowRight className="h-4 w-4 text-fit-moderate mt-0.5 flex-shrink-0" />
                <p className="text-sm text-bw-ink flex-1">{a.action}</p>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  a.effort === 'LOW' ? 'bg-success-bg text-success' :
                  a.effort === 'MEDIUM' ? 'bg-fit-moderate-bg text-fit-moderate' :
                  'bg-urgent-bg text-urgent'
                }`}>{a.effort}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {highImpact.length > 0 && (
        <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-4">
          <h3 className="font-semibold text-bw-primary mb-3 text-sm">High Impact</h3>
          <div className="space-y-2">
            {highImpact.map((a, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-bw-primary-tint rounded border border-blue-200">
                <ArrowRight className="h-4 w-4 text-bw-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-bw-ink">{a.action}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {differentiators.length > 0 && (
        <div className="bg-bw-surface rounded-lg shadow-sm border border-bw-border p-4">
          <h3 className="font-semibold text-bw-primary mb-3 text-sm">Differentiators</h3>
          <div className="space-y-2">
            {differentiators.map((a, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-bw-primary-tint rounded border border-purple-200">
                <ArrowRight className="h-4 w-4 text-bw-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-bw-ink">{a.action}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SourceGroundedFeedbackDisplay({
  winAssessment,
  gapReport,
  onNavigateToProfile
}: {
  winAssessment: WinAssessment;
  gapReport?: GapReport;
  onNavigateToProfile: (section: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className={`rounded-lg p-3 ${
          winAssessment.overallReadiness >= 70 ? 'bg-success-bg text-success' :
          winAssessment.overallReadiness >= 50 ? 'bg-fit-moderate-bg text-fit-moderate' :
          'bg-urgent-bg text-urgent'
        }`}>
          <p className="text-xs font-medium opacity-80">Readiness</p>
          <p className="text-2xl font-bold">{winAssessment.overallReadiness}%</p>
        </div>
        <div className="bg-bw-primary-tint rounded-lg p-3">
          <p className="text-xs text-bw-primary font-medium">Win Prob</p>
          <p className="text-2xl font-bold text-bw-primary">{winAssessment.winProbabilityEstimate}%</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-600 font-medium">Position</p>
          <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
            winAssessment.competitivePosition === 'STRONG' ? 'bg-success-bg text-success' :
            winAssessment.competitivePosition === 'MODERATE' ? 'bg-fit-moderate-bg text-fit-moderate' :
            'bg-urgent-bg text-urgent'
          }`}>{winAssessment.competitivePosition?.replace(/_/g, ' ')}</span>
        </div>
        <div className="bg-fit-moderate-bg rounded-lg p-3">
          <p className="text-xs text-fit-moderate font-medium">Critical</p>
          <p className="text-2xl font-bold text-fit-moderate">{gapReport?.summary?.criticalGapsCount || 0}</p>
        </div>
      </div>

      {winAssessment.factors && (
        <div className="bg-bw-surface rounded-lg border border-bw-border p-4">
          <h3 className="font-semibold text-bw-ink mb-3 flex items-center gap-2 text-sm">
            <TrendingUp className="h-4 w-4 text-bw-primary" /> Win Factors
          </h3>
          <div className="space-y-2">
            {Object.entries(winAssessment.factors).map(([key, score]) => (
              <div key={key} className="flex items-center gap-2">
                <span className="text-xs text-bw-ink flex-1 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${score >= 70 ? 'bg-success-bg0' : score >= 50 ? 'bg-fit-moderate-bg0' : 'bg-urgent-bg0'}`} style={{ width: `${score}%` }} />
                </div>
                <span className="text-xs font-medium w-8">{score}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {winAssessment.strengthAreas?.length > 0 && (
        <div className="bg-success-bg rounded-lg border border-green-200 p-4">
          <h3 className="font-semibold text-success mb-2 flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4" /> Strengths
          </h3>
          <ul className="space-y-1">
            {winAssessment.strengthAreas.slice(0, 4).map((s, i) => (
              <li key={i} className="text-sm text-success flex items-start gap-2">
                <span className="text-success mt-0.5">✓</span>{s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {winAssessment.weaknessAreas?.length > 0 && (
        <div className="bg-fit-moderate-bg rounded-lg border border-amber-200 p-4">
          <h3 className="font-semibold text-fit-moderate mb-2 flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4" /> Weaknesses
          </h3>
          <ul className="space-y-1">
            {winAssessment.weaknessAreas.slice(0, 4).map((w, i) => (
              <li key={i} className="text-sm text-fit-moderate flex items-start gap-2">
                <span className="text-fit-moderate mt-0.5">→</span>{w}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
